import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

const API = process.env.REACT_APP_API || "/api";

/* --------------------------------------------------------------------- i18n */

const L = {
  fr: {
    gateTitle: "Cours et coût matière",
    gateSub: "Les coûts viennent des prix réellement payés aux fournisseurs, remis à jour chaque soir.",
    user: "Identifiant", pass: "Mot de passe", signin: "Se connecter", signing: "Connexion…",
    pwd: { button: "Mot de passe", title: "Changer mon mot de passe", current: "Mot de passe actuel", next: "Nouveau mot de passe", again: "Confirmer le nouveau", save: "Enregistrer", saving: "Enregistrement…", done: "Mot de passe changé. Il sert dès la prochaine connexion.", mismatch: "Les deux nouveaux mots de passe ne correspondent pas.", short: "Au moins 8 caractères." },
    quit: "Quitter", loading: "Chargement…", computing: "Calcul…", close: "Fermer",
    print: "Imprimer", excel: "Excel", exporting: "Export…",
    tabSim: "Prix de vente", tabFood: "Coût matière", tabProg: "Déroulé", tabRec: "Recettes",
    imp: {
      button: "+ Importer une fiche", title: "Importer une fiche recette", back: "← Retour aux recettes",
      intro: "Excel (.xlsx) ou PDF enregistré depuis Excel, au gabarit des fiches chefs : titre, rendement, sections en gras avec leur total, étapes dans la colonne de droite. Rien n'est écrit dans Odoo avant le bouton Créer.",
      chef: "Chef", file: "Fiche", read: "Lire la fiche", reading: "Lecture…", readErr: "Fichier illisible.",
      history: "Derniers imports", hDate: "Date", hRecipe: "Recette", hChef: "Chef", hStatus: "Statut",
      failed: "échec", draftSt: "lu, non créé", original: "Fichier",
      dup: (d, c) => `Ce fichier a déjà été importé le ${d} (${c}). Le créer à nouveau ferait un doublon.`,
      name: "Nom de la recette", yieldQ: "Pièces par fournée", yieldU: "Unité", baking: "Cuisson",
      notes: "Notes (fiche produit Odoo)",
      sOk: "rattachés", sCheck: "à vérifier", sNew: "nouveaux", sSection: "sous-recettes", sIgnored: "ignorés",
      sheetTotal: "total de la fiche", steps: (n) => `${n} étape${n > 1 ? "s" : ""}`,
      direct: "Composants directs du produit fini", directNote: "après le total d'une section : beurre de tourage, finitions…",
      colLine: "Ligne de la fiche", colGrams: "Grammes", colLink: "Produit Odoo",
      ignore: "— Ignorer —", newIng: "➕ Nouvel ingrédient", suggestions: "Suggestions",
      sectionsOf: "Sous-recette de cette fiche", ingredients: "Ingrédients (ING-)", preps: "Préparations (PRP-)",
      noPrice: "sans prix", noPriceYet: "pas encore de prix dans Odoo",
      useSugg: (n, p) => `Rattacher à « ${n} » (${p} %)`, newName: "Nom (anglais)", priceKg: "CHF/kg (facultatif)",
      errGrams: "grammes manquants", errLink: "produit à choisir", errName: "nom manquant",
      errTitle: "Nom de la recette manquant", errChef: "Chef manquant", errYield: "Rendement manquant",
      cancel: "Annuler", create: "Créer dans Odoo", creating: "Création…",
      doneTitle: "Recette créée dans Odoo", doneCount: (p, n) => `${p} sous-recette${p > 1 ? "s" : ""} · ${n} nouvel${n > 1 ? "s" : ""} ingrédient${n > 1 ? "s" : ""}`,
      newIngs: "Nouveaux ingrédients", newIngsNote: "à relier à un article fournisseur",
      sqlNote: "Le SQL ref.seg_ingredient se joue sur le 242 (base prix_caa) ; renseigner ensuite le fournisseur et le code article pour que le prix suive les factures. En attendant, le prix se saisit à la main dans Odoo (Coût, par g).",
      dlSql: "SQL 242 (ref.seg_ingredient)", another: "Importer une autre fiche", see: "Voir la recette",
      reuseNew: "Créer une nouvelle préparation (PRP-)", reuseSame: "formule identique",
      reuseUse: (n, c) => `Réutiliser ${n} · ${c}`, reuseAll: "Toutes les préparations",
      reuseNote: (c) => `Rien n'est recréé : la préparation existante ${c} est utilisée telle quelle, avec la quantité de la fiche.`,
      reuseAuto: "Déjà dans Odoo avec la même formule", sReused: "préparations réutilisées",
      reusedTitle: "Préparations existantes réutilisées", foundTitle: "Ingrédients retrouvés (pas recréés)",
      st: { ok: "rattaché", check: "à vérifier", suggest: "suggestion", new: "nouveau", section: "sous-recette", qty: "quantité à préciser" },
    },
    ipNote: (chef) => `© ${chef ? `Chef ${chef} — ` : ""}Culinary Arts Academy Switzerland. Recette protégée : seuls le chef et CAA en détiennent les droits. Toute reproduction, modification ou diffusion sans leur accord écrit est interdite.`,
    chefAll: "Tous les chefs", chefNone: "Sans chef",
    noProd: "Aucune recette au programme de ce cours pour l'instant. Ajoute-les depuis l'onglet Prix de vente.",
    progRecipes: "Recettes au programme", progAdd: "+ Ajouter une recette…",
    progRemove: (n) => `Retirer « ${n} » du programme ?`, progNone: "Aucune recette pour l'instant.", chefBy: (n) => `Recette du chef ${n}`,
    daysLabel: "Jours de cours", nRecipes: "Recettes au programme",
    realRecipes: (n) => `Les ${n} recettes réellement chargées dans Odoo.`,
    extrapolated: (n, c) =>
      `Au-delà des ${n} recettes chargées, la matière est estimée à ${c} par recette. Une recette qui réutilise une pâte déjà pétrie coûtera moins.`,
    floorPrice: "Prix plancher", recoPrice: "Prix recommandé", premPrice: "Prix premium",
    floorNote: "en dessous, la session n'est pas viable",
    recoNote: "marge cible de l'école",
    premNote: "petit groupe, chef invité",
    fullCost: "Coût complet", perParticipant: (v) => `${v} par participant`,
    breakevenAt: "Équilibre à", breakevenNote: "participants, au prix recommandé",
    foodShare: "Matière par participant",
    foodShareNote: (p, d) => `${p} du prix de vente · ${d} par jour de cours`,
    recipesKpi: "Recettes", eachRecipe: (v) => `${v} de matière chacune`,
    productionCost: "Coût de production",
    productionSub: "ce que coûte la matière des recettes du cours",
    perRecipe: "par recette", totalFood: "Matière du cours",
    recipeCol: "Recette", batchCol: "Fournées", pieceCol: "Pièces", costCol: "Coût",
    unitCol: "La pièce",
    toDefine: (n) => `${n} recette${n > 1 ? "s" : ""} à définir`,
    toDefineNote: (c) => `estimée${""} au coût moyen des recettes chargées, ${c} chacune`,
    allowanceRow: "Provision d'apprentissage",
    daysNote: "Le nombre de jours ne change pas la matière : il pèse sur le chef, le laboratoire et la plonge, plus bas.",
    pooledNote: (a, b) =>
      `La somme des recettes fait ${a}. Le total du cours est de ${b} parce qu'une pâte partagée n'est pétrie qu'une fois et qu'on arrondit à la fournée entière.`,
    otherCosts: "Les autres coûts", otherSub: "déplacez les curseurs, le prix suit",
    sellingPrice: "Ce que ça donne comme prix",
    whereMoney: "Où va l'argent", sessionCost: "Coût complet de la session",
    marginAt: (p) => `Marge à ${p} la place`,
    moneyNote: "Les coûts d'enseignement et de laboratoire suivent le nombre de jours. La matière suit le nombre de recettes et l'effectif. L'acquisition et la conception, elles, ne bougent pas : c'est ce qui rend les petites cohortes chères.",
    breakeven: "Le seuil de rentabilité",
    breakevenCaption: (ts) =>
      `Chaque barre est un effectif. La marge grimpe à chaque inscrit puis retombe d'un cran quand une place de plus ouvre une équipe supplémentaire : une fournée entière part, qu'elle soit remplie ou non. Les équipes sont de ${ts}.`,
    equilibrium: (n) => `équilibre à ${n}`,
    gridTitle: "Effectif et prix", gridSub: "prix de vente par participant",
    cohort: "Effectif",
    gridNote: "Chaque case donne la marge de la session en francs, et en dessous sa part du chiffre d'affaires. La case encadrée est la situation actuelle. Lue en colonnes, la table montre l'effet du prix ; lue en lignes, celui de l'effectif — et c'est l'effectif qui pèse le plus.",
    hypotheses: "Hypothèses",
    hypNote: "Tout est modifiable : taux horaires, coût du laboratoire, commission, marges visées. Les prix matière, eux, viennent d'Odoo et ne se saisissent pas ici.",
    save: "Enregistrer", saved: "À jour", savingTxt: "Enregistrement…",
    minimumFood: (n) => n === 1
      ? "Une préparation n'a pas encore de prix : le coût matière affiché est un minimum."
      : `${n} préparations n'ont pas encore de prix : le coût matière affiché est un minimum.`,
    foodTitle: (p, tm) => `Coût matière, ${p} participants en ${tm} équipes`,
    overprod: "Surproduction", overprodNote: "de la matière",
    gapOdoo: "Écart avec Odoo", gapNote: "fournées entières vs proportionnel",
    sharedCount: "Sous-recettes partagées", sharedNote: "pétries une seule fois",
    foodIntro: (a, b) =>
      `L'aperçu de nomenclature d'Odoo chiffre chaque recette dans son coin et accepte de consommer 981 g d'une pâte qui se pétrit par 991. Une équipe ne fait pas 0,99 fournée. Ici les besoins de chaque préparation sont additionnés à travers toutes les recettes du cours, puis arrondis à la fournée entière — ce qui donne ${a} au lieu de ${b}.`,
    mep: "Mise en place", mepSub: "ce que les équipes pétrissent, une fois pour tout le cours",
    prep: "Préparation", need: "Besoin", oneBatch: "Une fournée", toMake: "À faire",
    produced: "Produit", surplus: "Surplus", cost: "Coût",
    sharedWith: (r) => `partagée par ${r}`,
    boughtIng: "Ingrédients achetés", lines: "lignes",
    ingredient: "Ingrédient", supplier: "Fournisseur", qty: "Quantité",
    price: "Prix", total: "Total", noPrice: "prix manquant",
    recovered: "récupération · déjà comptée", bySupplier: "Par fournisseur",
    photos: "Photos", lg: "fr", chefNote: "Note du chef",
    watchOn: "Voir sur YouTube",
    progTitle: (p, tm, s) => `${p} participants, ${tm} équipes de ${s}`,
    progFood: "CHF de matière", progTeams: "équipes",
    progCoverage: (m, tt) => `${m} productions chiffrées sur ${tt}`,
    day: "Jour", dayPrep: "mise en place",
    dayPrepNote: "Les pâtes et appareils préparés le premier jour. Leur matière est portée par les produits finis du jour suivant : elle n'est jamais comptée deux fois.",
    production: "Production", batches: "Fournées", pieces: "Pièces", food: "Matière",
    toSupply: "Recette à fournir",
    batchGives: (n) => `une fournée donne ${n} pièces`,
    perBatch: "la fournée", perPiece: "la pièce", allergens: "Allergènes",
    ingredientsN: (n) => `${n} ingrédients`,
    method: "Méthode", steps: (n) => `${n} étapes`,
    bakingTitle: "Cuisson et façonnage",
    daysWord: "jours", teamsOf: "équipes de",
  },
  en: {
    gateTitle: "Courses and food cost",
    gateSub: "Costs come from prices actually paid to suppliers, refreshed every evening.",
    user: "Username", pass: "Password", signin: "Sign in", signing: "Signing in…",
    pwd: { button: "Password", title: "Change my password", current: "Current password", next: "New password", again: "Confirm new password", save: "Save", saving: "Saving…", done: "Password changed. It applies from your next sign-in.", mismatch: "The two new passwords do not match.", short: "At least 8 characters." },
    quit: "Sign out", loading: "Loading…", computing: "Computing…", close: "Close",
    print: "Print", excel: "Excel", exporting: "Exporting…",
    tabSim: "Selling price", tabFood: "Food cost", tabProg: "Schedule", tabRec: "Recipes",
    imp: {
      button: "+ Import a recipe sheet", title: "Import a recipe sheet", back: "← Back to recipes",
      intro: "Excel (.xlsx) or a PDF saved from Excel, in the chefs' sheet layout: title, yield, bold sections with their total, steps in the right-hand column. Nothing is written to Odoo before the Create button.",
      chef: "Chef", file: "Sheet", read: "Read the sheet", reading: "Reading…", readErr: "Unreadable file.",
      history: "Recent imports", hDate: "Date", hRecipe: "Recipe", hChef: "Chef", hStatus: "Status",
      failed: "failed", draftSt: "read, not created", original: "File",
      dup: (d, c) => `This file was already imported on ${d} (${c}). Creating it again would duplicate it.`,
      name: "Recipe name", yieldQ: "Pieces per batch", yieldU: "Unit", baking: "Baking",
      notes: "Notes (Odoo product sheet)",
      sOk: "linked", sCheck: "to check", sNew: "new", sSection: "sub-recipes", sIgnored: "ignored",
      sheetTotal: "sheet total", steps: (n) => `${n} step${n > 1 ? "s" : ""}`,
      direct: "Direct components of the finished product", directNote: "after a section total: lamination butter, finishing…",
      colLine: "Sheet line", colGrams: "Grams", colLink: "Odoo product",
      ignore: "— Ignore —", newIng: "➕ New ingredient", suggestions: "Suggestions",
      sectionsOf: "Sub-recipe of this sheet", ingredients: "Ingredients (ING-)", preps: "Preparations (PRP-)",
      noPrice: "no price", noPriceYet: "no price in Odoo yet",
      useSugg: (n, p) => `Link to “${n}” (${p} %)`, newName: "Name (English)", priceKg: "CHF/kg (optional)",
      errGrams: "grams missing", errLink: "choose a product", errName: "name missing",
      errTitle: "Recipe name missing", errChef: "Chef missing", errYield: "Yield missing",
      cancel: "Cancel", create: "Create in Odoo", creating: "Creating…",
      doneTitle: "Recipe created in Odoo", doneCount: (p, n) => `${p} sub-recipe${p > 1 ? "s" : ""} · ${n} new ingredient${n > 1 ? "s" : ""}`,
      newIngs: "New ingredients", newIngsNote: "to link to a supplier item",
      sqlNote: "Run the ref.seg_ingredient SQL on server 242 (prix_caa), then fill in supplier and item code so the price follows invoices. Meanwhile, set the price by hand in Odoo (Cost, per g).",
      dlSql: "SQL 242 (ref.seg_ingredient)", another: "Import another sheet", see: "See the recipe",
      reuseNew: "Create a new preparation (PRP-)", reuseSame: "identical formula",
      reuseUse: (n, c) => `Reuse ${n} · ${c}`, reuseAll: "All preparations",
      reuseNote: (c) => `Nothing is recreated: the existing preparation ${c} is used as is, with the sheet quantity.`,
      reuseAuto: "Already in Odoo with the same formula", sReused: "preparations reused",
      reusedTitle: "Existing preparations reused", foundTitle: "Ingredients found (not recreated)",
      st: { ok: "linked", check: "to check", suggest: "suggestion", new: "new", section: "sub-recipe", qty: "quantity needed" },
    },
    ipNote: (chef) => `© ${chef ? `Chef ${chef} — ` : ""}Culinary Arts Academy Switzerland. Protected recipe: all rights belong solely to the chef and CAA. No reproduction, modification or distribution without their written consent.`,
    chefAll: "All chefs", chefNone: "No chef",
    noProd: "No recipe on this course's programme yet. Add them from the Selling price tab.",
    progRecipes: "Recipes on the programme", progAdd: "+ Add a recipe…",
    progRemove: (n) => `Remove “${n}” from the programme?`, progNone: "No recipe yet.", chefBy: (n) => `A recipe by Chef ${n}`,
    daysLabel: "Course days", nRecipes: "Recipes on the programme",
    realRecipes: (n) => `The ${n} recipes actually loaded in Odoo.`,
    extrapolated: (n, c) =>
      `Beyond the ${n} loaded recipes, food cost is estimated at ${c} per recipe. A recipe reusing a dough already mixed will cost less.`,
    floorPrice: "Floor price", recoPrice: "Recommended price", premPrice: "Premium price",
    floorNote: "below this, the session is not viable",
    recoNote: "the school's target margin",
    premNote: "small group, guest chef",
    fullCost: "Full cost", perParticipant: (v) => `${v} per participant`,
    breakevenAt: "Break-even at", breakevenNote: "participants, at the recommended price",
    foodShare: "Food per participant",
    foodShareNote: (p, d) => `${p} of the selling price · ${d} per teaching day`,
    recipesKpi: "Recipes", eachRecipe: (v) => `${v} of food each`,
    productionCost: "Production cost",
    productionSub: "what the food for the course recipes costs",
    perRecipe: "per recipe", totalFood: "Course food cost",
    recipeCol: "Recipe", batchCol: "Batches", pieceCol: "Pieces", costCol: "Cost",
    unitCol: "Per piece",
    toDefine: (n) => `${n} recipe${n > 1 ? "s" : ""} still to define`,
    toDefineNote: (c) => `estimated at the average of the loaded recipes, ${c} each`,
    allowanceRow: "Training allowance",
    daysNote: "The number of days does not change the food cost: it drives the chef, the kitchen and the stewarding, further down.",
    pooledNote: (a, b) =>
      `The recipes add up to ${a}. The course total is ${b} because a shared dough is mixed only once and quantities are rounded to whole batches.`,
    otherCosts: "The other costs", otherSub: "move the sliders, the price follows",
    sellingPrice: "What that gives as a price",
    whereMoney: "Where the money goes", sessionCost: "Full cost of the session",
    marginAt: (p) => `Margin at ${p} a seat`,
    moneyNote: "Teaching and kitchen costs follow the number of days. Food follows the number of recipes and the cohort. Marketing and course design do not move at all, which is what makes small cohorts expensive.",
    breakeven: "Break-even",
    breakevenCaption: (ts) =>
      `Each bar is a cohort size. Margin climbs with every booking, then drops a step when one more seat opens another team: a whole batch is mixed whether it is filled or not. Teams are of ${ts}.`,
    equilibrium: (n) => `break-even at ${n}`,
    gridTitle: "Cohort and price", gridSub: "selling price per participant",
    cohort: "Cohort",
    gridNote: "Each cell shows the session margin in francs, and below it as a share of revenue. The boxed cell is the current situation. Read down a column for the effect of price, across a row for the effect of cohort size — and cohort size matters more.",
    hypotheses: "Assumptions",
    hypNote: "Everything here is editable: hourly rates, kitchen cost, commission, target margins. Food prices come from Odoo and are not entered here.",
    save: "Save", saved: "Up to date", savingTxt: "Saving…",
    minimumFood: (n) => n === 1
      ? "One preparation has no price yet: the food cost shown is a minimum."
      : `${n} preparations have no price yet: the food cost shown is a minimum.`,
    foodTitle: (p, tm) => `Food cost, ${p} participants in ${tm} teams`,
    overprod: "Overproduction", overprodNote: "of food cost",
    gapOdoo: "Gap with Odoo", gapNote: "whole batches vs pro rata",
    sharedCount: "Shared sub-recipes", sharedNote: "mixed only once",
    foodIntro: (a, b) =>
      `Odoo's bill-of-materials view costs each recipe on its own and happily consumes 981 g of a dough mixed in 991 g batches. A team does not make 0.99 of a batch. Here each preparation's requirement is added up across every recipe of the course, then rounded to whole batches, which gives ${a} instead of ${b}.`,
    mep: "Mise en place", mepSub: "what the teams mix, once for the whole course",
    prep: "Preparation", need: "Required", oneBatch: "One batch", toMake: "To make",
    produced: "Produced", surplus: "Surplus", cost: "Cost",
    sharedWith: (r) => `shared by ${r}`,
    boughtIng: "Purchased ingredients", lines: "lines",
    ingredient: "Ingredient", supplier: "Supplier", qty: "Quantity",
    price: "Price", total: "Total", noPrice: "no price",
    recovered: "recovered · already costed", bySupplier: "By supplier",
    photos: "Photos", lg: "en", chefNote: "Chef's note",
    watchOn: "Watch on YouTube",
    progTitle: (p, tm, s) => `${p} participants, ${tm} teams of ${s}`,
    progFood: "CHF of food", progTeams: "teams",
    progCoverage: (m, tt) => `${m} productions costed out of ${tt}`,
    day: "Day", dayPrep: "mise en place",
    dayPrepNote: "The doughs and fillings prepared on the first day. Their cost is carried by the finished products of the next day: it is never counted twice.",
    production: "Production", batches: "Batches", pieces: "Pieces", food: "Food",
    toSupply: "Recipe still to be supplied",
    batchGives: (n) => `one batch yields ${n} pieces`,
    perBatch: "per batch", perPiece: "per piece", allergens: "Allergens",
    ingredientsN: (n) => `${n} ingredients`,
    method: "Method", steps: (n) => `${n} steps`,
    bakingTitle: "Baking and shaping",
    daysWord: "days", teamsOf: "teams of",
  },
};

/* -------------------------------------------------------------------- utils */

let LANG = "fr";
const nf = (d) =>
  new Intl.NumberFormat(LANG === "en" ? "en-CH" : "fr-CH",
    { minimumFractionDigits: d, maximumFractionDigits: d });

const chf = (v) => (v == null ? "—" : nf(2).format(v));
const chf0 = (v) => (v == null ? "—" : nf(0).format(v));
const pct = (v) => (v == null ? "—" : `${nf(1).format(v)} %`);

const weight = (g) => {
  if (g == null) return "—";
  if (g >= 1000) return `${nf(g % 1000 === 0 ? 0 : 2).format(g / 1000)} kg`;
  return `${nf(g % 1 === 0 ? 0 : 1).format(g)} g`;
};

const perKg = (p) => (p ? `${nf(2).format(p * 1000)} / kg` : null);

async function call(path, { token, method = "GET", body } = {}) {
  const res = await fetch(`${API}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch (e) {
    data = null;
  }
  if (!res.ok) throw new Error((data && data.detail) || `Erreur ${res.status}`);
  return data;
}

async function download(path, token, filename) {
  const res = await fetch(`${API}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Erreur ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const STRATA = [
  "#B5761F", "#8C5A16", "#D9A441", "#6E4A12",
  "#234B3A", "#35664F", "#4E7D64", "#1A3A2C",
  "#A8563A", "#C0492B",
];

/* ------------------------------------------------------------------ chrome */

function LangToggle({ lang, setLang }) {
  return (
    <div className="lang no-print">
      {["fr", "en"].map((k) => (
        <button key={k} className={lang === k ? "on" : ""} onClick={() => setLang(k)}>
          {k.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

function Toolbar({ onPrint, onExport, busy, t }) {
  return (
    <div className="toolbar no-print">
      <button onClick={onPrint}>{t.print}</button>
      <button onClick={onExport} disabled={busy}>{busy ? t.exporting : t.excel}</button>
    </div>
  );
}

function Login({ onIn, t, lang, setLang }) {
  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      onIn(await call("/login", { method: "POST", body: { user, password } }));
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="gate">
      <div className="gate-card">
        <LangToggle lang={lang} setLang={setLang} />
        <h1 className="gate-title">{t.gateTitle}</h1>
        <p className="gate-sub">{t.gateSub}</p>
        <label className="field">
          <span>{t.user}</span>
          <input value={user} autoFocus autoComplete="off"
                 onChange={(e) => setUser(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && submit()} />
        </label>
        <label className="field">
          <span>{t.pass}</span>
          <input type="password" value={password}
                 onChange={(e) => setPassword(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && submit()} />
        </label>
        {error && <p className="gate-error">{error}</p>}
        <button className="btn-main" onClick={submit} disabled={busy || !user}>
          {busy ? t.signing : t.signin}
        </button>
      </div>
    </div>
  );
}

function PasswordModal({ token, t, onClose }) {
  const tp = t.pwd;
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    setError(null);
    if (next.length < 8) return setError(tp.short);
    if (next !== again) return setError(tp.mismatch);
    setBusy(true);
    try {
      await call("/password", { token, method: "POST", body: { current, new: next } });
      setDone(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const enter = (e) => e.key === "Enter" && submit();

  return (
    <div className="pwd-overlay no-print" onClick={onClose}>
      <div className="gate-card" onClick={(e) => e.stopPropagation()}>
        <h1 className="gate-title">{tp.title}</h1>
        {done ? (
          <>
            <p className="gate-sub">{tp.done}</p>
            <button className="btn-main" onClick={onClose}>{t.close}</button>
          </>
        ) : (
          <>
            <label className="field">
              <span>{tp.current}</span>
              <input type="password" value={current} autoFocus autoComplete="current-password"
                     onChange={(e) => setCurrent(e.target.value)} onKeyDown={enter} />
            </label>
            <label className="field">
              <span>{tp.next}</span>
              <input type="password" value={next} autoComplete="new-password"
                     onChange={(e) => setNext(e.target.value)} onKeyDown={enter} />
            </label>
            <label className="field">
              <span>{tp.again}</span>
              <input type="password" value={again} autoComplete="new-password"
                     onChange={(e) => setAgain(e.target.value)} onKeyDown={enter} />
            </label>
            {error && <p className="gate-error">{error}</p>}
            <div className="pwd-actions">
              <button className="btn-main" onClick={submit} disabled={busy || !current || !next}>
                {busy ? tp.saving : tp.save}
              </button>
              <button className="btn-plain" onClick={onClose}>{t.close}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- breakeven */

function Breakeven({ data, current, t }) {
  const curve = data.curve || [];
  if (curve.length === 0) return null;
  const W = 720, H = 190, pad = 26;
  const max = Math.max(...curve.map((p) => Math.abs(p.margin)), 1);
  const bw = (W - pad * 2) / curve.length;
  const zero = H / 2;

  return (
    <figure className="chart">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={t.breakeven}>
        <line x1={pad} y1={zero} x2={W - pad} y2={zero} stroke="#16120E" strokeWidth="1" />
        {curve.map((p, i) => {
          const h = (Math.abs(p.margin) / max) * (H / 2 - pad);
          const x = pad + i * bw;
          const isNow = p.n === current;
          return (
            <g key={p.n}>
              <rect x={x + 1} y={p.margin >= 0 ? zero - h : zero}
                    width={Math.max(bw - 2, 2)} height={Math.max(h, 1)}
                    fill={p.margin >= 0 ? "#234B3A" : "#C0492B"}
                    opacity={isNow ? 1 : 0.32} />
              {(p.n % 2 === 0 || isNow) && (
                <text x={x + bw / 2} y={H - 6} textAnchor="middle" fontSize="10"
                      fill={isNow ? "#16120E" : "#7A7167"}
                      fontWeight={isNow ? 700 : 400}>{p.n}</text>
              )}
            </g>
          );
        })}
        {data.participants && (
          <text x={pad + (data.participants - 0.5) * bw} y={14} textAnchor="middle"
                fontSize="11" fill="#16120E" fontWeight="700">
            {t.equilibrium(data.participants)}
          </text>
        )}
      </svg>
      <figcaption>{t.breakevenCaption(data.team_size)}</figcaption>
    </figure>
  );
}

/* -------------------------------------------------------------------- grid */

function Grid({ grid, current, t }) {
  const cls = (m) => (m < 0 ? "neg" : m < 1000 ? "thin" : "good");
  return (
    <div className="gridwrap">
      <table className="table grid">
        <thead>
          <tr>
            <th>{t.cohort}</th>
            {grid.rows[0].cells.map((c) => (
              <th key={c.price_chf} className="num">
                {chf0(c.price_chf)}
                <em>{chf0(c.price_usd)} $</em>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grid.rows.map((row) => (
            <tr key={row.n} className={row.n === current ? "here" : ""}>
              <td>{row.n}</td>
              {row.cells.map((c) => (
                <td key={c.price_chf} className={`num cell ${cls(c.margin)}`}>
                  {chf0(c.margin)}
                  <em>{pct(c.margin_pct)}</em>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ params */

const FIELD_GROUPS = (lang) => {
  const en = lang === "en";
  return [
    [en ? "Target margins" : "Marges visées", [
      ["min_margin_pct", en ? "Minimum margin" : "Marge minimale", "%"],
      ["target_margin_pct", en ? "Target margin" : "Marge cible", "%"],
      ["premium_margin_pct", en ? "Premium margin" : "Marge premium", "%"],
      ["fx_usd_chf", en ? "1 USD in CHF" : "1 USD en CHF", ""],
    ]],
    [en ? "Food" : "Matière", [
      ["training_allowance_pct", en ? "Training allowance" : "Provision d'apprentissage", "%"],
    ]],
    [en ? "Teaching, per day" : "Enseignement, par jour", [
      ["chef_hours_per_day", en ? "Teaching hours" : "Heures de cours", "h"],
      ["chef_prep_hours_per_day", en ? "Preparation hours" : "Heures de préparation", "h"],
      ["chef_rate_chf", en ? "Chef rate" : "Taux chef", "CHF / h"],
      ["assistant_hours_per_day", en ? "Assistant hours" : "Heures assistant", "h"],
      ["assistant_rate_chf", en ? "Assistant rate" : "Taux assistant", "CHF / h"],
      ["cleaning_hours_per_day", en ? "Stewarding hours" : "Heures plonge", "h"],
      ["cleaning_rate_chf", en ? "Stewarding rate" : "Taux plonge", "CHF / h"],
    ]],
    [en ? "Kitchen, per day" : "Laboratoire, par jour", [
      ["lab_chf_per_day", en ? "Kitchen" : "Laboratoire", "CHF"],
      ["energy_chf_per_day", en ? "Energy" : "Énergie", "CHF"],
    ]],
    [en ? "Hospitality" : "Hospitalité", [
      ["hospitality_enabled", en ? "Include (1 or 0)" : "Inclure (1 ou 0)", ""],
      ["nights_per_participant", en ? "Nights" : "Nuits", "/ pers."],
      ["night_cost_chf", en ? "Internal cost of a night" : "Coût interne d'une nuit", "CHF"],
      ["meals_per_day", en ? "Meals per day" : "Repas par jour", ""],
      ["meal_cost_chf", en ? "Internal cost of a meal" : "Coût interne d'un repas", "CHF"],
      ["coffee_breaks_per_day", en ? "Breaks per day" : "Pauses par jour", ""],
      ["coffee_cost_chf", en ? "Cost of a break" : "Coût d'une pause", "CHF"],
    ]],
    [en ? "Commercial and overhead" : "Commercial et structure", [
      ["kit_chf_per_participant", en ? "Kit and certificate" : "Kit et certificat", "CHF / pers."],
      ["marketing_chf", en ? "Marketing per session" : "Acquisition par session", "CHF"],
      ["commission_pct", en ? "Sales commission" : "Commission de vente", "%"],
      ["payment_fee_pct", en ? "Payment fees" : "Frais de paiement", "%"],
      ["overhead_pct", en ? "Overhead" : "Frais de structure", "%"],
      ["contingency_pct", en ? "Contingency" : "Provision d'aléas", "%"],
      ["design_cost_chf", en ? "Course design" : "Conception du cours", "CHF"],
      ["amortize_sessions", en ? "Amortised over" : "Amortie sur", "sessions"],
    ]],
  ];
};

function Params({ economics, onSave, saving, t, lang }) {
  const [draft, setDraft] = useState(economics);
  const [open, setOpen] = useState(false);
  useEffect(() => setDraft(economics), [economics]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(economics);

  return (
    <section className="params no-print">
      <button className="params-toggle" onClick={() => setOpen(!open)} aria-expanded={open}>
        {t.hypotheses} {open ? "—" : "+"}
      </button>
      {!open && <p className="note">{t.hypNote}</p>}
      {open && (
        <>
          <div className="params-grid">
            {FIELD_GROUPS(lang).map(([group, rows]) => (
              <div key={group} className="params-group">
                <h3>{group}</h3>
                {rows.map(([key, label, unit]) => (
                  <label key={key} className="param">
                    <span>{label}</span>
                    <span className="param-in">
                      <input value={draft[key]} inputMode="decimal"
                             onChange={(e) =>
                               setDraft({ ...draft, [key]: e.target.value.replace(",", ".") })} />
                      <em>{unit}</em>
                    </span>
                  </label>
                ))}
              </div>
            ))}
          </div>
          <button className="btn-main" disabled={!dirty || saving} onClick={() => onSave(draft)}>
            {saving ? t.savingTxt : dirty ? t.save : t.saved}
          </button>
        </>
      )}
    </section>
  );
}

/* --------------------------------------------------------------- simulation */

function Lever({ label, value, onChange, min, max }) {
  return (
    <label className="lever">
      <span>{label}</span>
      <span className="cohort">
        <button onClick={() => onChange(Math.max(min, value - 1))}>–</button>
        <input value={value} inputMode="numeric"
               onChange={(e) => {
                 const v = parseInt(e.target.value, 10);
                 onChange(isNaN(v) ? min : Math.min(max, Math.max(min, v)));
               }} />
        <button onClick={() => onChange(Math.min(max, value + 1))}>+</button>
      </span>
    </label>
  );
}

function Slider({ label, value, min, max, step, unit, onChange }) {
  return (
    <label className="slider">
      <span className="slider-top">
        <span>{label}</span>
        <strong>{value}<em>{unit}</em></strong>
      </span>
      <input type="range" min={min} max={max} step={step} value={value}
             onChange={(e) => onChange(parseFloat(e.target.value))} />
    </label>
  );
}

const DRIVERS = (lang) => {
  const en = lang === "en";
  return [
    ["chef_rate_chf", en ? "Chef rate" : "Taux chef", 40, 200, 5, " CHF/h"],
    ["chef_prep_hours_per_day", en ? "Prep hours per day" : "Heures de préparation par jour", 0, 8, 0.5, " h"],
    ["lab_chf_per_day", en ? "Kitchen per day" : "Laboratoire par jour", 0, 1200, 25, " CHF"],
    ["marketing_chf", en ? "Marketing per session" : "Acquisition par session", 0, 5000, 100, " CHF"],
    ["overhead_pct", en ? "Overhead" : "Frais de structure", 0, 30, 1, " %"],
    ["target_margin_pct", en ? "Target margin" : "Marge cible", 10, 70, 1, " %"],
  ];
};

function Simulation({ data, food, days, setDays, token, courseId, onChanged, onError,
                     over, setOver, onSave, saving, t, lang }) {
  if (!data) return <p className="note">{t.computing}</p>;
  const s = data.sim;
  const P = data.margins;
  const foodBlock = s.blocks.find((b) => b.lines["Matière première"] || b.lines["Matière première"] === 0)
    || s.blocks[0];
  const foodPerHead = s.participants ? foodBlock.total / s.participants : null;
  const recipeSum = food && food.recipes
    ? food.recipes.reduce((a, r) => a + (r.alone_cost || 0), 0) : 0;
  const foodVals = Object.values(foodBlock.lines);
  const rawMaterial = foodVals[0] || 0;
  const allowance = foodVals[1] || 0;
  const extraRecipes = Math.max(0, s.recipes - s.real_recipes);

  return (
    <>
      <section className="levers no-print">
        <Lever label={t.daysLabel} value={days} onChange={setDays} min={1} max={10} />
        <ProgrammeEditor token={token} courseId={courseId}
                         inCourse={(food && food.recipes) || []}
                         onChanged={onChanged} onError={onError} t={t} />
      </section>

      <section className="verdict">
        <div className="verdict-fig">
          <span className="verdict-label">{t.totalFood}</span>
          <span className="verdict-value">{chf0(foodBlock.total)}<em>CHF</em></span>
          <span className="verdict-foot">
            {t.perParticipant(chf(foodPerHead))} · {chf(s.material_per_recipe)} {t.perRecipe}
          </span>
        </div>
        <dl className="kpis">
          <div>
            <dt>{t.recipesKpi}</dt>
            <dd>{(food && food.recipes && food.recipes.length) || 0}</dd>
            <span>{lang === "en" ? "on the programme" : "au programme"}</span>
          </div>
          <div>
            <dt>{lang === "en" ? "Teams" : "Équipes"}</dt>
            <dd>{s.teams}</dd>
            <span>{s.participants} {lang === "en" ? "participants" : "participants"}</span>
          </div>
          <div>
            <dt>{lang === "en" ? "Per teaching day" : "Par jour de cours"}</dt>
            <dd>{chf0(s.material_per_day)}</dd>
            <span>{pct(s.food_cost_pct)} {lang === "en" ? "of the price" : "du prix de vente"}</span>
          </div>
        </dl>
      </section>

      {food && food.recipes && food.recipes.length > 0 && (
        <section>
          <h2 className="h2">{t.productionCost}<span>{t.productionSub}</span></h2>
          <table className="table">
            <thead>
              <tr>
                <th>{t.recipeCol}</th>
                <th className="num">{t.batchCol}</th>
                <th className="num">{t.pieceCol}</th>
                <th className="num">{t.unitCol}</th>
                <th className="num">{t.costCol}</th>
              </tr>
            </thead>
            <tbody>
              {food.recipes.map((r) => (
                <tr key={r.code}>
                  <td>{r.name}<em>{r.code}</em></td>
                  <td className="num">{r.batches}</td>
                  <td className="num">{chf0(r.pieces)}</td>
                  <td className="num strong-num">
                    {r.pieces ? chf(r.alone_cost / r.pieces) : "—"}
                  </td>
                  <td className="num">{chf(r.alone_cost)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              {extraRecipes > 0 && (
                <tr className="est">
                  <td colSpan={4}>
                    {t.toDefine(extraRecipes)}
                    <em>{t.toDefineNote(chf(s.material_per_recipe))}</em>
                  </td>
                  <td className="num">{chf(extraRecipes * s.material_per_recipe)}</td>
                </tr>
              )}
              <tr>
                <td colSpan={4}>{t.totalFood}</td>
                <td className="num">{chf(rawMaterial)}</td>
              </tr>
              {allowance > 0 && (
                <tr className="soft-foot">
                  <td colSpan={4}>{t.allowanceRow}</td>
                  <td className="num">{chf(allowance)}</td>
                </tr>
              )}
              <tr className="grand">
                <td colSpan={4}>{t.totalFood} · {s.participants} × {food.recipes.length}</td>
                <td className="num">{chf(foodBlock.total)}</td>
              </tr>
            </tfoot>
          </table>
          {!s.extrapolated && Math.abs(recipeSum - food.material_cost) > 0.5 && (
            <p className="note">{t.pooledNote(chf(recipeSum), chf(food.material_cost))}</p>
          )}
          <p className="note">{t.daysNote}</p>
        </section>
      )}

      <section>
        <h2 className="h2">{t.otherCosts}<span>{t.otherSub}</span></h2>
        <div className="sliders">
          {DRIVERS(lang).map(([key, label, min, max, step, unit]) => (
            <Slider key={key} label={label} min={min} max={max} step={step} unit={unit}
                    value={over[key] !== undefined ? over[key] : data.economics[key]}
                    onChange={(v) => setOver({ ...over, [key]: v })} />
          ))}
        </div>
      </section>

      <section>
        <h2 className="h2">{t.sellingPrice}</h2>
        <div className="prices">
          {[
            ["minimum", t.floorPrice, P.min, t.floorNote],
            ["recommande", t.recoPrice, P.target, t.recoNote],
            ["premium", t.premPrice, P.premium, t.premNote],
          ].map(([k, label, pctv, note]) => (
            <div key={k} className={`price ${k === "recommande" ? "is-main" : ""}`}>
              <span className="price-label">{label}</span>
              <span className="price-value">{chf0(s.prices[k])}<em>CHF</em></span>
              <span className="price-usd">≈ {chf0(s.prices_usd[k])} USD</span>
              <span className="price-note">{pct(pctv)} · {note}</span>
            </div>
          ))}
        </div>
        <dl className="kpis wide">
          <div>
            <dt>{t.fullCost}</dt>
            <dd>{chf0(s.full_cost)}</dd>
            <span>{t.perParticipant(chf(s.cost_per_participant))}</span>
          </div>
          <div>
            <dt>{t.breakevenAt}</dt>
            <dd>{data.breakeven.participants || "—"}</dd>
            <span>{t.breakevenNote}</span>
          </div>
          <div>
            <dt>{t.foodShare}</dt>
            <dd>{chf(foodPerHead)}</dd>
            <span>{t.foodShareNote(pct(s.food_cost_pct), chf(s.material_per_day))}</span>
          </div>
        </dl>
      </section>

      {(s.unpriced || []).length > 0 && (
        <p className="warn block">{t.minimumFood(s.unpriced.length)}</p>
      )}

      <div className="two">
        <section>
          <h2 className="h2">{t.whereMoney}</h2>
          <table className="table pnl">
            <tbody>
              {s.blocks.map((b) => (
                <React.Fragment key={b.name}>
                  <tr className="blockhead">
                    <td>{b.name}</td>
                    <td className="num">{chf0(b.total)}</td>
                  </tr>
                  {Object.entries(b.lines).map(([k, v]) => (
                    <tr key={k}>
                      <td className="sub">{k}</td>
                      <td className="num soft">{chf0(v)}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
              <tr className="blockhead total">
                <td>{t.sessionCost}</td>
                <td className="num">{chf0(s.full_cost)}</td>
              </tr>
              {Object.entries(s.selling).map(([k, v]) => (
                <tr key={k}>
                  <td className="sub">{k}</td>
                  <td className="num soft">{chf0(v)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>{t.marginAt(chf0(s.price_chf))}</td>
                <td className={`num ${s.margin >= 0 ? "" : "negtext"}`}>
                  {chf0(s.margin)} · {pct(s.margin_pct)}
                </td>
              </tr>
            </tfoot>
          </table>
          <p className="note">{t.moneyNote}</p>
        </section>

        <section>
          <h2 className="h2">{t.breakeven}</h2>
          <Breakeven data={data.breakeven} current={s.participants} t={t} />
        </section>
      </div>

      <section>
        <h2 className="h2">{t.gridTitle}<span>{t.gridSub}</span></h2>
        <Grid grid={data.grid} current={s.participants} t={t} />
        <p className="note">{t.gridNote}</p>
      </section>

      <Params economics={{ ...data.economics, ...over }} onSave={onSave}
              saving={saving} t={t} lang={lang} />
    </>
  );
}

/* ---------------------------------------------------------------- food cost */

function FoodCost({ data, t }) {
  if (!data) return <p className="note">{t.loading}</p>;
  if (!data.coverage || !data.coverage.total) return <p className="note">{t.noProd}</p>;
  const mep = data.mise_en_place || [];

  return (
    <>
      <section className="verdict">
        <div className="verdict-fig">
          <span className="verdict-label">{t.foodTitle(data.participants, data.teams)}</span>
          <span className="verdict-value">{chf0(data.material_cost)}<em>CHF</em></span>
          <span className="verdict-foot">
            {t.perParticipant(chf(data.per_participant))} · {mep.length} {t.mep.toLowerCase()}
          </span>
        </div>
        <dl className="kpis">
          <div>
            <dt>{t.overprod}</dt>
            <dd>{chf0(data.surplus_cost)}</dd>
            <span>{pct(data.surplus_pct)} {t.overprodNote}</span>
          </div>
          <div>
            <dt>{t.gapOdoo}</dt>
            <dd>{data.rounding_gap >= 0 ? "+" : ""}{chf0(data.rounding_gap)}</dd>
            <span>{t.gapNote}</span>
          </div>
          <div>
            <dt>{t.sharedCount}</dt>
            <dd>{data.shared_count}</dd>
            <span>{t.sharedNote}</span>
          </div>
        </dl>
      </section>

      <p className="note block-note">
        {t.foodIntro(chf0(data.material_cost), chf0(data.alone_cost))}
      </p>

      <section>
        <h2 className="h2">{t.mep}<span>{t.mepSub}</span></h2>
        <table className="table">
          <thead>
            <tr>
              <th>{t.prep}</th>
              <th className="num">{t.need}</th>
              <th className="num">{t.oneBatch}</th>
              <th className="num">{t.toMake}</th>
              <th className="num">{t.surplus}</th>
              <th className="num">{t.cost}</th>
            </tr>
          </thead>
          <tbody>
            {mep.map((m, k) => (
              <tr key={k}>
                <td>
                  {m.name}
                  <em>{m.code}{m.shared && ` · ${t.sharedWith(m.used_by.join(", "))}`}</em>
                </td>
                <td className="num">{weight(m.required)}</td>
                <td className="num soft">{weight(m.batch_qty)}</td>
                <td className="num"><strong>{m.batches}</strong></td>
                <td className={`num ${m.surplus_pct > 15 ? "negtext" : "soft"}`}>
                  {m.surplus > 0 ? `${weight(m.surplus)} · ${chf(m.surplus_cost)}` : "—"}
                </td>
                <td className="num">{chf(m.cost)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section>
        <h2 className="h2">{t.boughtIng}<span>{data.ingredients.length} {t.lines}</span></h2>
        <table className="table">
          <thead>
            <tr>
              <th>{t.ingredient}</th>
              <th>{t.supplier}</th>
              <th className="num">{t.qty}</th>
              <th className="num">{t.price}</th>
              <th className="num">{t.cost}</th>
            </tr>
          </thead>
          <tbody>
            {data.ingredients.map((g, k) => (
              <tr key={k} className={g.recovered ? "recovered"
                                     : g.priced === false ? "no-price" : ""}>
                <td>{g.name}<em>{g.code}</em></td>
                <td className="sup">{g.supplier || "—"}</td>
                <td className="num">{weight(g.qty)}</td>
                <td className="num soft">
                  {g.recovered ? t.recovered
                   : g.priced === false ? t.noPrice : perKg(g.unit_cost)}
                </td>
                <td className="num">{chf(g.cost)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={4}>{t.total}</td>
              <td className="num">{chf(data.material_cost)}</td>
            </tr>
          </tfoot>
        </table>
      </section>

      {data.by_supplier.length > 0 && (
        <section>
          <h2 className="h2">{t.bySupplier}</h2>
          <table className="table">
            <tbody>
              {data.by_supplier.map((s) => (
                <tr key={s.supplier}>
                  <td>{s.supplier}</td>
                  <td className="num soft">{s.items} {t.lines}</td>
                  <td className="num">{chf(s.cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}

/* --------------------------------------------------------------- programme */

function Programme({ course, food, showCosts, t }) {
  if (!course.items || !course.items.length) return <p className="note">{t.noProd}</p>;
  const days = [...new Set(course.items.map((i) => i.day))].sort();
  const mep = (food && food.mise_en_place) || [];

  return (
    <>
      <section className="verdict">
        <div className="verdict-fig">
          <span className="verdict-label">
            {t.progTitle(course.participants, course.teams, course.team_size)}
          </span>
          <span className="verdict-value">
            {showCosts ? chf0(course.material_cost) : course.teams}
            <em>{showCosts ? t.progFood : t.progTeams}</em>
          </span>
          <span className="verdict-foot">
            {t.progCoverage(course.coverage.mapped, course.coverage.total)}
          </span>
        </div>
      </section>

      {days.map((d) => {
        const rows = course.items.filter((i) => i.day === d && i.status === "ok");
        const isPrep = rows.length === 0 && mep.length > 0;
        return (
          <section key={d}>
            <h2 className="h2">
              {t.day} {d}
              {isPrep && <span>{t.dayPrep}</span>}
            </h2>

            {isPrep ? (
              <>
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t.prep}</th>
                      <th className="num">{t.oneBatch}</th>
                      <th className="num">{t.toMake}</th>
                      <th className="num">{t.produced}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mep.map((m, k) => (
                      <tr key={k}>
                        <td>
                          {m.name}
                          <em>{m.code}{m.shared && ` · ${t.sharedWith(m.used_by.join(", "))}`}</em>
                        </td>
                        <td className="num soft">{weight(m.batch_qty)}</td>
                        <td className="num"><strong>{m.batches}</strong></td>
                        <td className="num">{weight(m.produced)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="note">{t.dayPrepNote}</p>
              </>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>{t.production}</th>
                    <th className="num">{t.batches}</th>
                    <th className="num">{t.pieces}</th>
                    {showCosts && <th className="num">{t.food}</th>}
                  </tr>
                </thead>
                <tbody>
                  {course.items.filter((i) => i.day === d).map((i, k) => (
                    <tr key={k} className={i.status === "ok" ? "" : "pending"}>
                      <td>
                        {i.label}
                        {i.note && <em>{i.note}</em>}
                        {i.status !== "ok" && !i.note && <em>{t.toSupply}</em>}
                      </td>
                      <td className="num">{i.status === "ok" ? i.batches : "—"}</td>
                      <td className="num">{i.pieces ? chf0(i.pieces) : "—"}</td>
                      {showCosts && (
                        <td className="num">{i.cost != null ? chf(i.cost) : "—"}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        );
      })}
    </>
  );
}

/* ------------------------------------------------------------------ recette */

/* Module vidéo d'une étape. youtube-nocookie : pas de cookie tant que
   l'étudiant n'a pas lancé la lecture. */
function VideoModule({ v, t }) {
  if (!v || !v.youtube) return null;
  const id = (t.lg === "en" && v.youtube_en) || v.youtube;
  const titre = (t.lg === "en" && v.title_en) || v.title || "";
  const sous = (t.lg === "en" && v.subtitle_en) || v.subtitle || "";
  return (
    <span className="step-video">
      {(titre || sous) && (
        <span className="vid-head">
          {titre && <b>{titre}</b>}
          {sous && <i>{sous}</i>}
          {v.duration && <u>{v.duration}</u>}
        </span>
      )}
      <span className="vid-frame">
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${id}?rel=0`}
          title={titre || "Module"}
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          loading="lazy"
        />
      </span>
      <a className="vid-link" href={`https://youtu.be/${id}`}
         target="_blank" rel="noreferrer">{t.watchOn}</a>
    </span>
  );
}

function Method({ text, t, inner, photos, chips, notes, videos }) {
  if (!text) return null;
  const steps = text.split("\n").filter(Boolean);
  const lg = t.lg;
  // un contenu peut exister dans les deux langues ; sans suffixe il sert aux deux
  const pick = (v) => (Array.isArray(v) || typeof v === "string" ? v
                       : v && (v[lg] || v[""] || Object.values(v)[0]));
  const shots = (n) => {
    const v = (photos || {})[n];
    if (!v) return [];
    if (typeof v === "string") return [v];                 // ancien format
    if (!Array.isArray(v)) return [pick(v)].filter(Boolean);
    return v.filter((x) => !x.lang || x.lang === lg).map((x) => x.url);
  };
  return (
    <div className={`method${inner ? " method-in" : ""}`}>
      <h4>{t.method}<span>{t.steps(steps.length)}</span></h4>
      <ol>
        {steps.map((line, i) => {
          const n = String(i + 1);
          const tags = pick((chips || {})[n]) || [];
          const note = pick((notes || {})[n]);
          const imgs = shots(n);
          return (
            <li key={i}>
              {line}
              {tags.length > 0 && (
                <span className="chips">
                  {tags.map((c, k) => <em key={k}>{c}</em>)}
                </span>
              )}
              <VideoModule v={(videos || {})[n]} t={t} />
              {imgs.map((u, k) => (
                <img className="step-shot" key={k} src={u} alt="" loading="lazy" />
              ))}
              {note && (
                <span className="chef-note"><b>{t.chefNote}</b> {note}</span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/* Galerie : les photos que les chefs déposent dans le dossier du serveur. */
function Shots({ urls, t }) {
  if (!urls || !urls.length) return null;
  return (
    <div className="shots">
      {urls.map((u, i) => <img key={i} src={u} alt="" loading="lazy" />)}
    </div>
  );
}

/* Une ligne de composant. Quand ce composant est lui-même une préparation,
   elle se déplie sur ses propres ingrédients et sa méthode — sans quoi une
   garniture prise dans une autre garniture resterait un cul-de-sac. */
function LeafRow({ leaf, showCosts, t }) {
  const [open, setOpen] = useState(false);
  const kids = leaf.children || [];
  const cols = showCosts ? 5 : 3;
  return (
    <>
      <tr className={leaf.recovered ? "recovered"
                     : leaf.priced === false ? "no-price" : ""}>
        <td className="l-name">
          {kids.length > 0 ? (
            <button type="button" className="l-open" onClick={() => setOpen(!open)}
                    aria-expanded={open}>
              <span className="l-caret">{open ? "−" : "+"}</span>
              {leaf.name}
              <span className="l-count">{t.ingredientsN(kids.length)}</span>
            </button>
          ) : leaf.name}
          <em>{leaf.code}</em>
        </td>
        <td className="l-qty">{weight(leaf.qty)}</td>
        {showCosts && (
          <td className="l-unit">
            {leaf.recovered ? t.recovered
             : leaf.priced === false ? t.noPrice : perKg(leaf.unit_cost)}
          </td>
        )}
        <td className="l-sup">{leaf.supplier || ""}</td>
        {showCosts && <td className="l-cost">{chf(leaf.cost)}</td>}
      </tr>
      {open && kids.length > 0 && (
        <tr className="l-nest">
          <td colSpan={cols}>
            <table className="leaves leaves-in">
              <tbody>
                {kids.map((k, i) => (
                  <LeafRow key={i} leaf={k} showCosts={showCosts} t={t} />
                ))}
              </tbody>
            </table>
            <Method text={leaf.method} t={t} inner photos={leaf.step_photos}
                    chips={leaf.step_chips} notes={leaf.step_notes}
                    videos={leaf.step_videos} />
            <Shots urls={leaf.photos} t={t} />
          </td>
        </tr>
      )}
    </>
  );
}

function Stratum({ node, color, share, open, onToggle, showCosts, t }) {
  const leaves = node.children || [];
  return (
    <div className={`stratum ${open ? "is-open" : ""}`}>
      <button className="stratum-band" style={{ "--band": color, "--share": `${share}%` }}
              onClick={onToggle} aria-expanded={open}>
        <span className="band-fill" />
        <span className="band-body">
          <span className="band-name">
            {node.name}
            {leaves.length > 0 && <span className="band-count">{t.ingredientsN(leaves.length)}</span>}
          </span>
          <span className="band-figures">
            <span className="band-qty">{weight(node.qty)}</span>
            {showCosts && <span className="band-cost">{chf(node.cost)}</span>}
          </span>
        </span>
      </button>
      {open && (
        <div className={`split ${node.method ? "" : "split-one"}`}>
          {leaves.length > 0 && (
            <div className="split-l">
              <table className="leaves">
                <tbody>
                  {leaves.map((leaf, i) => (
                    <LeafRow key={i} leaf={leaf} showCosts={showCosts} t={t} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {node.method && (
            <div className="split-r">
              <Method text={node.method} t={t} photos={node.step_photos}
                       chips={node.step_chips} notes={node.step_notes}
                       videos={node.step_videos} />
              <Shots urls={node.photos} t={t} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const NO_CHEF = "__none__";

function groupByChef(list, t) {
  const by = new Map();
  list.forEach((r) => {
    const k = r.chef || NO_CHEF;
    if (!by.has(k)) by.set(k, []);
    by.get(k).push(r);
  });
  return [...by.entries()]
    .sort(([a], [b]) => (a === NO_CHEF) - (b === NO_CHEF) || a.localeCompare(b, "fr"))
    .map(([key, recipes]) => ({ key, label: key === NO_CHEF ? t.chefNone : key, recipes }));
}

/* Programme éditable : les recettes du cours, ajoutées ou retirées ici,
   enregistrées dans courses.json. */
function ProgrammeEditor({ token, courseId, inCourse, onChanged, onError, t }) {
  const [library, setLibrary] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    call("/recipes", { token }).then((d) => setLibrary(d.recipes || [])).catch(() => {});
  }, [token]);

  const codes = new Set(inCourse.map((r) => r.code));
  const free = groupByChef(library.filter((r) => !codes.has(r.code)), t);
  const chefOf = Object.fromEntries(library.map((r) => [r.code, r.chef]));

  const edit = async (body) => {
    setBusy(true);
    try {
      await call(`/course/${courseId}/productions`, { token, method: "PUT", body });
      onChanged();
    } catch (e) {
      onError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="prog-edit">
      <span className="lever-label">{t.progRecipes} · {inCourse.length}</span>
      <div className="prog-chips">
        {inCourse.length === 0 && <span className="note">{t.progNone}</span>}
        {inCourse.map((r) => (
          <span key={r.code} className="prog-chip">
            {r.name}
            {chefOf[r.code] && <em>{chefOf[r.code]}</em>}
            <button disabled={busy} title="✕"
                    onClick={() => window.confirm(t.progRemove(r.name)) && edit({ remove: r.code })}>
              ×
            </button>
          </span>
        ))}
        {free.some((g) => g.recipes.length) && (
          <select className="prog-add" value="" disabled={busy}
                  onChange={(e) => e.target.value && edit({ add: e.target.value })}>
            <option value="">{t.progAdd}</option>
            {free.map((g) => (
              <optgroup key={g.key} label={g.label}>
                {g.recipes.map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
              </optgroup>
            ))}
          </select>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ import */

/* Importer une fiche de chef (Excel ou PDF) : lecture, aperçu corrigé à la
   main, puis création des ING-/PRP-/REC- dans Odoo seg. Rien n'est écrit dans
   Odoo avant le bouton « Créer ». */
const IMP_STATUS = {
  alias: "ok", exact: "ok", manual: "ok", proposed: "check", suggest: "suggest",
  new: "new", section: "section", qty: "qty",
};

function lineValue(l) {
  if (l.action === "link") return `link:${l.code}`;
  if (l.action === "section") return `sec:${l.section}`;
  return l.action || "ignore";
}

function lineIssue(l, ti) {
  if (l.action === "ignore") return null;
  if (!(parseFloat(l.grams) > 0)) return ti.errGrams;
  if (l.action === "link" && !l.code) return ti.errLink;
  if (l.action === "new" && !(l.new_name || "").trim()) return ti.errName;
  return null;
}

function ImportLine({ l, where, cands, sections, families, ti, onChange }) {
  const ings = cands.filter((c) => c.kind === "ING");
  const prps = cands.filter((c) => c.kind === "PRP");
  const byCode = Object.fromEntries(cands.map((c) => [c.code, c]));
  const status = IMP_STATUS[l.status] || "new";
  const issue = lineIssue(l, ti);
  const best = l.suggestions && l.suggestions[0];
  // le produit retenu s'affiche par son nom ; les autres suggestions gardent leur score
  const others = (l.suggestions || []).filter((s) => !(l.action === "link" && s.code === l.code));

  const pick = (v) => {
    if (v === "ignore" || v === "new") onChange({ action: v, status: v === "new" ? "new" : l.status });
    else if (v.startsWith("link:")) onChange({ action: "link", code: v.slice(5), status: "manual" });
    else if (v.startsWith("sec:")) onChange({ action: "section", section: v.slice(4), status: "section" });
  };

  return (
    <tr className={`imp-line ${l.action === "ignore" ? "ignored" : ""} ${issue ? "bad" : ""}`}>
      <td>
        <span className={`imp-dot ${status}`} title={ti.st[status]} />
        <span className="imp-raw">{l.name}</span>
        {l.raw_qty && String(l.raw_qty) !== String(l.grams) && <em>{l.raw_qty}</em>}
        {l.warn && <em className="warn">{l.warn}</em>}
      </td>
      <td className="num">
        <input className="imp-g" inputMode="decimal" value={l.grams == null ? "" : l.grams}
               onChange={(e) => {
                 const v = e.target.value.replace(",", ".");
                 const patch = { grams: v === "" ? null : v };
                 if (l.action === "ignore" && l.status === "qty" && parseFloat(v) > 0) {
                   patch.action = l.action_if_qty || "new";
                   patch.status = patch.action === "link" ? "proposed" : "new";
                 }
                 onChange(patch);
               }} />
        <span className="imp-unit">g</span>
      </td>
      <td>
        <select className="imp-pick" value={lineValue(l)} onChange={(e) => pick(e.target.value)}>
          <option value="ignore">{ti.ignore}</option>
          <option value="new">{ti.newIng}</option>
          {others.length > 0 && (
            <optgroup label={ti.suggestions}>
              {others.map((s) => (
                <option key={`s${s.code}`} value={`link:${s.code}`}>
                  {s.name} · {s.code} · {Math.round(s.score * 100)} %
                </option>
              ))}
            </optgroup>
          )}
          {sections.filter((s) => s.key !== where).length > 0 && (
            <optgroup label={ti.sectionsOf}>
              {sections.filter((s) => s.key !== where).map((s) => (
                <option key={s.key} value={`sec:${s.key}`}>↳ {s.name}</option>
              ))}
            </optgroup>
          )}
          <optgroup label={ti.ingredients}>
            {ings.map((c) => (
              <option key={c.code} value={`link:${c.code}`}>
                {c.name} · {c.code}{c.priced ? "" : ` · ${ti.noPrice}`}
              </option>
            ))}
          </optgroup>
          <optgroup label={ti.preps}>
            {prps.map((c) => <option key={c.code} value={`link:${c.code}`}>{c.name} · {c.code}</option>)}
          </optgroup>
        </select>
        {l.action === "link" && byCode[l.code] && !byCode[l.code].priced && (
          <em className="warn">{ti.noPriceYet}</em>
        )}
        {l.status === "suggest" && l.action === "new" && best && (
          <button className="imp-sugg" onClick={() => pick(`link:${best.code}`)}>
            {ti.useSugg(best.name, Math.round(best.score * 100))}
          </button>
        )}
        {l.action === "new" && (
          <div className="imp-new">
            <input value={l.new_name || ""} placeholder={ti.newName}
                   onChange={(e) => onChange({ new_name: e.target.value })} />
            <select value={l.new_family || "DRY"}
                    onChange={(e) => onChange({ new_family: e.target.value })}>
              {families.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
            <input className="imp-price" inputMode="decimal" placeholder={ti.priceKg}
                   value={l.new_price_kg == null ? "" : l.new_price_kg}
                   onChange={(e) => onChange({ new_price_kg: e.target.value.replace(",", ".") })} />
          </div>
        )}
        {issue && <em className="err">{issue}</em>}
      </td>
    </tr>
  );
}

function ImportTable({ lines, where, ...rest }) {
  return (
    <table className="table imp-table">
      <thead>
        <tr>
          <th>{rest.ti.colLine}</th>
          <th className="num">{rest.ti.colGrams}</th>
          <th>{rest.ti.colLink}</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((l) => (
          <ImportLine key={l.id} l={l} where={where} {...rest}
                      onChange={(patch) => rest.onLine(where, l.id, patch)} />
        ))}
      </tbody>
    </table>
  );
}

function Importer({ token, t, chefs, onClose, onDone }) {
  const ti = t.imp;
  const [step, setStep] = useState("upload");
  const [file, setFile] = useState(null);
  const [chef, setChef] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [meta, setMeta] = useState(null);     // réponse de lecture : id, candidats…
  const [draft, setDraft] = useState(null);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(() => {
    call("/imports", { token }).then((d) => setHistory(d.imports || [])).catch(() => {});
  }, [token]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const read = () => {
    if (!file) return;
    setBusy(true); setErr(null);
    const fr = new FileReader();
    fr.onload = async () => {
      try {
        const d = await call("/import/parse", {
          token, method: "POST",
          body: { filename: file.name, content_b64: fr.result, chef },
        });
        setMeta(d);
        setDraft({ ...d.draft, chef: d.draft.chef || chef });
        setStep("review");
      } catch (e) {
        setErr(e.message);
      } finally {
        setBusy(false);
      }
    };
    fr.onerror = () => { setErr(ti.readErr); setBusy(false); };
    fr.readAsDataURL(file);
  };

  const onLine = (where, id, patch) => {
    setDraft((d) => {
      const upd = (ls) => ls.map((l) => (l.id === id ? { ...l, ...patch } : l));
      if (where === "loose") return { ...d, loose: upd(d.loose) };
      return { ...d, sections: d.sections.map((s) => (s.key === where ? { ...s, lines: upd(s.lines) } : s)) };
    });
  };
  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setSection = (key, patch) =>
    setDraft((d) => ({ ...d, sections: d.sections.map((s) => (s.key === key ? { ...s, ...patch } : s)) }));

  const all = draft ? [...draft.sections.filter((s) => !s.reuse).flatMap((s) => s.lines), ...draft.loose] : [];
  const count = (f) => all.filter(f).length;
  const issues = draft ? [
    ...(!(draft.title || "").trim() ? [ti.errTitle] : []),
    ...(!(draft.chef || "").trim() ? [ti.errChef] : []),
    ...(!(parseFloat(draft.yield_qty) > 0) ? [ti.errYield] : []),
    ...all.map((l) => lineIssue(l, ti) && `${l.name} : ${lineIssue(l, ti)}`).filter(Boolean),
  ] : [];

  const create = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await call(`/import/${meta.id}/commit`, { token, method: "POST", body: { draft } });
      setResult(r);
      setStep("done");
      loadHistory();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const restart = () => {
    setStep("upload"); setFile(null); setMeta(null); setDraft(null); setResult(null); setErr(null);
  };

  const common = draft && meta && {
    cands: meta.candidates, sections: draft.sections, families: meta.families, ti, onLine,
  };

  return (
    <section className="imp">
      <div className="imp-head no-print">
        <h1 className="recipe-title">{ti.title}</h1>
        <button className="btn-plain" onClick={onClose}>{ti.back}</button>
      </div>
      {err && <div className="alert">{err}<button onClick={() => setErr(null)}>{t.close}</button></div>}

      {step === "upload" && (
        <>
          <p className="note">{ti.intro}</p>
          <div className="imp-upload">
            <label>
              <span>{ti.chef}</span>
              <input list="imp-chefs" value={chef} placeholder="Muriset, Jeremy Plassat…"
                     onChange={(e) => setChef(e.target.value)} />
              <datalist id="imp-chefs">{chefs.map((c) => <option key={c} value={c} />)}</datalist>
            </label>
            <label>
              <span>{ti.file}</span>
              <input type="file" accept=".xlsx,.pdf"
                     onChange={(e) => setFile(e.target.files && e.target.files[0])} />
            </label>
            <button className="imp-go" disabled={!file || busy} onClick={read}>
              {busy ? ti.reading : ti.read}
            </button>
          </div>

          {history.length > 0 && (
            <>
              <h2 className="h2">{ti.history}</h2>
              <table className="table">
                <thead>
                  <tr><th>{ti.hDate}</th><th>{ti.hRecipe}</th><th>{ti.hChef}</th>
                      <th>{ti.hStatus}</th><th /></tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id}>
                      <td className="soft">{h.at.replace("T", " ").slice(0, 16)}<em>{h.by}</em></td>
                      <td>{h.title || h.filename}<em>{h.filename}</em></td>
                      <td>{h.chef || "—"}</td>
                      <td>
                        {h.status === "committed" ? <strong>{h.rec_code}</strong>
                          : h.status === "failed" ? <span className="negtext" title={h.error}>{ti.failed}</span>
                          : <span className="soft">{ti.draftSt}</span>}
                      </td>
                      <td className="imp-dl">
                        {h.has_sql && (
                          <button className="btn-plain" onClick={() => download(`/import/${h.id}/ref.sql`, token,
                            `ref_seg_ingredient_import_${h.id}.sql`).catch((e) => setErr(e.message))}>SQL 242</button>
                        )}
                        <button className="btn-plain" onClick={() => download(`/import/${h.id}/file`, token,
                          h.filename).catch((e) => setErr(e.message))}>{ti.original}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </>
      )}

      {step === "review" && draft && (
        <>
          {meta.duplicate && (
            <div className="alert">{ti.dup(meta.duplicate.date, meta.duplicate.rec_code)}</div>
          )}
          {(draft.warnings || []).map((w, i) => <p key={i} className="note imp-warn">{w}</p>)}

          <div className="imp-fields">
            <label className="wide"><span>{ti.name}</span>
              <input value={draft.title} onChange={(e) => setField("title", e.target.value)} /></label>
            <label><span>{ti.chef}</span>
              <input list="imp-chefs2" value={draft.chef || ""} onChange={(e) => setField("chef", e.target.value)} />
              <datalist id="imp-chefs2">{(meta.chefs || []).map((c) => <option key={c} value={c} />)}</datalist></label>
            <label><span>{ti.yieldQ}</span>
              <input inputMode="decimal" value={draft.yield_qty == null ? "" : draft.yield_qty}
                     onChange={(e) => setField("yield_qty", e.target.value.replace(",", "."))} /></label>
            <label><span>{ti.yieldU}</span>
              <input value={draft.yield_unit || ""} onChange={(e) => setField("yield_unit", e.target.value)} /></label>
            <label className="wide"><span>{ti.baking}</span>
              <input value={draft.baking || ""} onChange={(e) => setField("baking", e.target.value)} /></label>
            <label className="wide"><span>{ti.notes}</span>
              <textarea rows={2} value={draft.notes || ""} onChange={(e) => setField("notes", e.target.value)} /></label>
          </div>

          <div className="imp-summary">
            <span><i className="imp-dot ok" />{count((l) => l.action === "link" && IMP_STATUS[l.status] === "ok")} {ti.sOk}</span>
            <span><i className="imp-dot check" />{count((l) => l.action === "link" && l.status === "proposed")} {ti.sCheck}</span>
            <span><i className="imp-dot new" />{count((l) => l.action === "new")} {ti.sNew}</span>
            <span><i className="imp-dot section" />{count((l) => l.action === "section")} {ti.sSection}</span>
            <span><i className="imp-dot ok" />{draft.sections.filter((s) => s.reuse).length} {ti.sReused}</span>
            <span><i className="imp-dot qty" />{count((l) => l.action === "ignore")} {ti.sIgnored}</span>
          </div>

          {draft.sections.map((s) => (
            <div key={s.key} className="imp-section">
              <div className="imp-sec-head">
                <input className="imp-sec-name" value={s.name}
                       onChange={(e) => setSection(s.key, { name: e.target.value })} />
                <select className={`imp-reuse ${s.reuse ? "on" : ""}`} value={s.reuse || ""}
                        onChange={(e) => setSection(s.key, { reuse: e.target.value || null })}>
                  <option value="">{ti.reuseNew}</option>
                  {(s.reuse_suggestions || []).map((r) => (
                    <option key={`r${r.code}`} value={r.code}>
                      {ti.reuseUse(r.name, r.code)}{r.same ? ` · ${ti.reuseSame}` : ` · ${Math.round(r.score * 100)} %`}
                    </option>
                  ))}
                  <optgroup label={ti.reuseAll}>
                    {meta.candidates.filter((c) => c.kind === "PRP"
                      && !(s.reuse_suggestions || []).some((r) => r.code === c.code)).map((c) => (
                      <option key={c.code} value={c.code}>{ti.reuseUse(c.name, c.code)}</option>
                    ))}
                  </optgroup>
                </select>
                <span className="soft">
                  {chf0(s.lines.filter((l) => l.action !== "ignore")
                    .reduce((a, l) => a + (parseFloat(l.grams) || 0), 0))} g
                  {s.total ? ` · ${ti.sheetTotal} ${chf0(s.total)} g` : ""}
                </span>
              </div>
              {s.reuse ? (
                <p className="note imp-reused">
                  {s.reuse_status === "same" && s.reuse === (s.reuse_suggestions[0] || {}).code
                    ? <strong>{ti.reuseAuto}. </strong> : null}
                  {ti.reuseNote(s.reuse)}
                </p>
              ) : (
                <div className="imp-scroll"><ImportTable lines={s.lines} where={s.key} {...common} /></div>
              )}
              {s.steps && s.steps.length > 0 && (
                <details className="imp-steps">
                  <summary>{ti.steps(s.steps.length)}</summary>
                  <ol>{s.steps.map((st, i) => <li key={i}>{st}</li>)}</ol>
                </details>
              )}
            </div>
          ))}

          {draft.loose.length > 0 && (
            <div className="imp-section">
              <div className="imp-sec-head"><strong>{ti.direct}</strong>
                <span className="soft">{ti.directNote}</span></div>
              <div className="imp-scroll"><ImportTable lines={draft.loose} where="loose" {...common} /></div>
            </div>
          )}

          <div className="imp-foot">
            {issues.length > 0 && (
              <ul className="imp-issues">{issues.slice(0, 8).map((x, i) => <li key={i}>{x}</li>)}
                {issues.length > 8 && <li>… {issues.length - 8}</li>}</ul>
            )}
            <button className="btn-plain" onClick={restart}>{ti.cancel}</button>
            <button className="imp-go" disabled={busy || issues.length > 0} onClick={create}>
              {busy ? ti.creating : ti.create}
            </button>
          </div>
        </>
      )}

      {step === "done" && result && (
        <>
          <section className="verdict">
            <div className="verdict-fig">
              <span className="verdict-label">{ti.doneTitle}</span>
              <span className="verdict-value">{result.rec_code}<em>{result.rec_name}</em></span>
              <span className="verdict-foot">
                {ti.doneCount(result.products.filter((p) => p.kind === "PRP").length,
                              result.new_ingredients.length)}
              </span>
            </div>
          </section>
          {(result.reused_preparations || []).length > 0 && (
            <>
              <h2 className="h2">{ti.reusedTitle}</h2>
              <table className="table"><tbody>
                {result.reused_preparations.map((x) => (
                  <tr key={x.code}><td>{x.section}<em>{x.code}</em></td>
                    <td className="num">{chf0(x.grams)} g</td></tr>
                ))}
              </tbody></table>
            </>
          )}
          {(result.reused_ingredients || []).length > 0 && (
            <>
              <h2 className="h2">{ti.foundTitle}</h2>
              <table className="table"><tbody>
                {result.reused_ingredients.map((x) => (
                  <tr key={x.code}><td>{x.name}<em>{x.code}</em></td>
                    <td className="soft">{x.asked}</td></tr>
                ))}
              </tbody></table>
            </>
          )}
          {result.new_ingredients.length > 0 && (
            <>
              <h2 className="h2">{ti.newIngs}<span>{ti.newIngsNote}</span></h2>
              <table className="table">
                <tbody>
                  {result.new_ingredients.map((n) => (
                    <tr key={n.code}>
                      <td>{n.name}<em>{n.code} · {n.family}</em></td>
                      <td className="num">{n.price_kg ? `${chf(n.price_kg)} CHF/kg` : <span className="negtext">{ti.noPrice}</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="note">{ti.sqlNote}</p>
            </>
          )}
          <div className="imp-foot">
            {result.ref_sql && (
              <button className="btn-plain" onClick={() => download(`/import/${result.id}/ref.sql`, token,
                `ref_seg_ingredient_import_${result.id}.sql`).catch((e) => setErr(e.message))}>
                {ti.dlSql}
              </button>
            )}
            <button className="btn-plain" onClick={restart}>{ti.another}</button>
            <button className="imp-go" onClick={() => onDone(result.rec_code)}>{ti.see}</button>
          </div>
        </>
      )}
    </section>
  );
}

function Recipes({ token, showCosts, t, onCode }) {
  const [list, setList] = useState([]);
  const [code, setCode] = useState(null);
  const [recipe, setRecipe] = useState(null);
  const [open, setOpen] = useState({});
  const [chef, setChef] = useState(null); // null = tous les chefs
  const [importing, setImporting] = useState(false);
  const [reload, setReload] = useState(0);
  const want = useRef(null); // recette à afficher après un import

  // Les recettes regroupées par chef auteur (étiquette « Chef … » dans Odoo).
  // Ordre alphabétique, les recettes sans chef en dernier.
  const groups = useMemo(() => groupByChef(list, t), [list, t]);

  const hasChefs = groups.some((g) => g.key !== NO_CHEF);
  const shown = chef ? groups.filter((g) => g.key === chef) : groups;

  const pickChef = (k) => {
    setChef(k);
    const g = k ? groups.find((x) => x.key === k) : groups[0];
    if (g && g.recipes.length && !g.recipes.some((r) => r.code === code)) {
      setCode(g.recipes[0].code);
    }
  };

  useEffect(() => {
    call("/recipes", { token }).then((d) => {
      const rs = d.recipes || [];
      setList(rs);
      const target = want.current && rs.find((r) => r.code === want.current);
      want.current = null;
      if (target) { setChef(null); setCode(target.code); }
      else if (rs.length) setCode((c) => (c && rs.some((r) => r.code === c) ? c : rs[0].code));
    });
  }, [token, reload]);

  useEffect(() => {
    if (!code) return;
    setOpen({});
    call(`/recipe/${code}`, { token }).then(setRecipe);
  }, [code, token]);

  // l'export Excel doit sortir la recette affichée, pas les six
  useEffect(() => {
    if (onCode) onCode(code);
  }, [code, onCode]);

  const strata = useMemo(() => {
    if (!recipe) return [];
    const items = recipe.components || [];
    const max = Math.max(...items.map((c) => c.qty || 0), 1);
    return items.map((c, i) => ({
      node: c,
      color: STRATA[i % STRATA.length],
      share: Math.max(6, ((c.qty || 0) / max) * 100),
    }));
  }, [recipe]);

  const chefNames = groups.filter((g) => g.key !== NO_CHEF).map((g) => g.key);
  if (importing) {
    return (
      <Importer token={token} t={t} chefs={chefNames} onClose={() => setImporting(false)}
                onDone={(rec) => { want.current = rec; setImporting(false); setReload((n) => n + 1); }} />
    );
  }
  const importBtn = showCosts && (
    <div className="imp-open no-print">
      <button className="btn-plain" onClick={() => setImporting(true)}>{t.imp.button}</button>
    </div>
  );

  if (!recipe) return <>{importBtn}<p className="note">{t.loading}</p></>;

  return (
    <>
      {importBtn}
      {hasChefs && (
        <div className="chefs no-print">
          <button className={`chef-tab ${chef === null ? "on" : ""}`} onClick={() => pickChef(null)}>
            {t.chefAll} <span>{list.length}</span>
          </button>
          {groups.map((g) => (
            <button key={g.key} className={`chef-tab ${chef === g.key ? "on" : ""}`}
                    onClick={() => pickChef(g.key)}>
              {g.label} <span>{g.recipes.length}</span>
            </button>
          ))}
        </div>
      )}

      <div className="no-print">
        {shown.map((g) => (
          <div key={g.key} className="chef-group">
            {hasChefs && !chef && <h5 className="chef-label">{g.label}</h5>}
            <div className="pickers">
              {g.recipes.map((r) => (
                <button key={r.code} className={`pick ${r.code === code ? "on" : ""}`}
                        onClick={() => setCode(r.code)}>
                  {r.name}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {recipe.chef && <p className="chef-by">{t.chefBy(recipe.chef)}</p>}
      <h1 className="recipe-title">{recipe.name}</h1>
      <p className="note">
        {recipe.code} · {t.batchGives(chf0(recipe.batch_qty))}
        {showCosts && (
          <>
            {" · "}<strong>{chf(recipe.total_cost)}</strong> {t.perBatch},{" "}
            <strong>{chf(recipe.cost_per_piece)}</strong> {t.perPiece}
          </>
        )}
      </p>

      <p className="ip-note">{t.ipNote(recipe.chef)}</p>

      <Shots urls={recipe.photos} t={t} />

      <div className="strata">
        {strata.map(({ node, color, share }, i) => (
          <Stratum key={i} node={node} color={color} share={share} showCosts={showCosts}
                   open={!!open[i]} onToggle={() => setOpen({ ...open, [i]: !open[i] })} t={t} />
        ))}
      </div>

      {recipe.baking && (
        <div className="baking">
          <h4>{t.bakingTitle}</h4>
          <ul>
            {recipe.baking.split("\n").filter(Boolean).map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </div>
      )}

      {recipe.method && (
        <div className="method standalone">
          <h4>{t.method}</h4>
          <ol>
            {recipe.method.split("\n").filter(Boolean).map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ol>
        </div>
      )}

      {recipe.nutrition && recipe.nutrition.allergens && (
        <p className="note">{t.allergens} : {recipe.nutrition.allergens}</p>
      )}
    </>
  );
}

/* ----------------------------------------------------------------- the page */

const SECTION = { sim: "simulation", food: "foodcost", prog: "programme", recipes: "recipes" };

export default function App() {
  // L'anglais par défaut : le cours vise le marché américain et les fiches de
  // Muriset sont en anglais. Le choix de l'utilisateur est retenu par navigateur.
  const [lang, setLang] = useState(() => {
    try {
      const v = window.localStorage.getItem("seg-lang");
      if (v === "fr" || v === "en") return v;
    } catch (e) { /* navigation privée */ }
    return "en";
  });
  useEffect(() => {
    try { window.localStorage.setItem("seg-lang", lang); } catch (e) { /* ignore */ }
  }, [lang]);
  const [session, setSession] = useState(null);
  const [courseId, setCourseId] = useState(null);
  const [courseList, setCourseList] = useState([]);
  const [course, setCourse] = useState(null);
  const [sim, setSim] = useState(null);
  const [food, setFood] = useState(null);
  const [days, setDays] = useState(0);
  const [recipes, setRecipes] = useState(0);
  const [over, setOver] = useState({});
  const [tab, setTab] = useState("sim");
  const [participants, setParticipants] = useState(10);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [recipeCode, setRecipeCode] = useState(null);
  const [pwdOpen, setPwdOpen] = useState(false);
  const timer = useRef(null);

  LANG = lang;
  const t = L[lang];
  const token = session && session.token;
  const showCosts = session && session.role !== "student";

  useEffect(() => {
    if (!token) return;
    call("/courses", { token })
      .then((d) => {
        const list = d.courses || [];
        setCourseList(list);
        let keep = null;
        try { keep = window.localStorage.getItem("seg-course"); } catch (e) { /* ignore */ }
        const first = list.find((c) => c.id === keep) || list[0];
        if (first) {
          setCourseId(first.id);
          setParticipants(first.min_cohort || 10);
        }
      })
      .catch((e) => setError(e.message));
  }, [token]);

  const load = useCallback(() => {
    if (!token || !courseId) return;
    call(`/course/${courseId}?participants=${participants}`, { token })
      .then(setCourse)
      .catch((e) => setError(e.message));
    if (showCosts) {
      const overStr = Object.entries(over).map(([k, v]) => `${k}:${v}`).join(",");
      const q = new URLSearchParams({ participants, days, recipes });
      if (overStr) q.set("over", overStr);
      call(`/course/${courseId}/simulation?${q.toString()}`, { token })
        .then((d) => {
          setSim(d);
          if (!days) setDays(d.sim.days);
        })
        .catch((e) => setError(e.message));
      call(`/course/${courseId}/foodcost?participants=${participants}`, { token })
        .then(setFood)
        .catch((e) => setError(e.message));
    }
  }, [token, courseId, participants, showCosts, days, recipes, over]);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(load, 200);
    return () => clearTimeout(timer.current);
  }, [load]);

  // Changer de cours repart de ses propres réglages : durée, nombre de
  // recettes et surcharges des curseurs appartiennent au cours affiché.
  const switchCourse = (id) => {
    const c = courseList.find((x) => x.id === id);
    if (!c || id === courseId) return;
    setCourse(null); setSim(null); setFood(null);
    setDays(0); setRecipes(0); setOver({});
    setParticipants(c.min_cohort || 10);
    setCourseId(id);
    try { window.localStorage.setItem("seg-course", id); } catch (e) { /* ignore */ }
  };

  const saveEconomics = async (draft) => {
    setSaving(true);
    try {
      await call(`/course/${courseId}/economics`, {
        token, method: "PUT", body: { economics: { ...draft, ...over } },
      });
      setOver({});
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const doExport = async () => {
    setExporting(true);
    try {
      const section = SECTION[tab] || "all";
      const one = section === "recipes" && recipeCode ? recipeCode : "";
      const q = new URLSearchParams({ section, participants, days, recipes, lang });
      if (one) q.set("recipe", one);
      await download(`/course/${courseId}/export.xlsx?${q.toString()}`, token,
                     one ? `${one.toLowerCase()}.xlsx`
                         : `${courseId}-${section}-${participants}p.xlsx`);
    } catch (e) {
      setError(e.message);
    } finally {
      setExporting(false);
    }
  };

  if (!session) {
    return <><Style /><Login onIn={setSession} t={t} lang={lang} setLang={setLang} /></>;
  }

  const tabs = showCosts
    ? [["sim", t.tabSim], ["food", t.tabFood], ["prog", t.tabProg], ["recipes", t.tabRec]]
    : [["prog", t.tabProg], ["recipes", t.tabRec]];
  const activeTab = tabs.some(([k]) => k === tab) ? tab : tabs[0][0];

  return (
    <>
      <Style />
      <header className="top">
        <div className="top-id">
          {courseList.length > 1 ? (
            <select className="course-pick no-print" value={courseId || ""}
                    onChange={(e) => switchCourse(e.target.value)}>
              {courseList.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          ) : null}
          <strong className={courseList.length > 1 ? "print-only" : ""}>
            {course ? course.name : "…"}
          </strong>
          {course && (
            <span>
              {course.days} {t.daysWord} × {course.hours_per_day} h · {t.teamsOf}{" "}
              {course.team_size}
            </span>
          )}
        </div>
        <nav className="tabs no-print">
          {tabs.map(([k, label]) => (
            <button key={k} className={activeTab === k ? "on" : ""} onClick={() => setTab(k)}>
              {label}
            </button>
          ))}
        </nav>
        <div className="top-right no-print">
          <span className="cohort">
            <button onClick={() => setParticipants(Math.max(1, participants - 1))}>–</button>
            <input value={participants} inputMode="numeric"
                   onChange={(e) => {
                     const v = parseInt(e.target.value, 10);
                     setParticipants(isNaN(v) ? 1 : Math.min(60, Math.max(1, v)));
                   }} />
            <button onClick={() => setParticipants(Math.min(60, participants + 1))}>+</button>
          </span>
          <LangToggle lang={lang} setLang={setLang} />
          {session.role !== "student" && (
            <button className="btn-plain" onClick={() => setPwdOpen(true)}>{t.pwd.button}</button>
          )}
          <button className="btn-plain" onClick={() => setSession(null)}>{t.quit}</button>
        </div>
      </header>
      {pwdOpen && <PasswordModal token={token} t={t} onClose={() => setPwdOpen(false)} />}

      {error && (
        <div className="alert no-print">
          {error}
          <button onClick={() => setError(null)}>{t.close}</button>
        </div>
      )}

      <main className="page">
        <Toolbar onPrint={() => window.print()} onExport={doExport} busy={exporting} t={t} />

        {activeTab === "sim" && (
          <Simulation data={sim} food={food} days={days || 2} setDays={setDays}
                      token={token} courseId={courseId} onChanged={load} onError={setError}
                      over={over} setOver={setOver}
                      onSave={saveEconomics} saving={saving} t={t} lang={lang} />
        )}
        {activeTab === "food" && <FoodCost data={food} t={t} />}
        {activeTab === "prog" && course && (
          <Programme course={course} food={food} showCosts={showCosts} t={t} />
        )}
        {activeTab === "recipes" && <Recipes token={token} showCosts={showCosts} t={t}
                                            onCode={setRecipeCode} />}
        {!course && !error && <p className="note">{t.loading}</p>}
      </main>
    </>
  );
}

/* --------------------------------------------------------------------- CSS */

function Style() {
  return (
    <style>{`
:root{
  --ink:#16120E;        /* noir chaud, pas bleuté */
  --paper:#FFFDFA;
  --wash:#F1EBE1;       /* fond, ton pain */
  --rule:#DFD5C6;
  --muted:#7A7167;
  --gold:#B5761F;       /* accent : caramel, croûte */
  --gold-lt:#D9A441;
  --green:#234B3A;      /* argent qui rentre */
  --rust:#C0492B;       /* alerte */
  --display:Georgia,"Times New Roman",serif;
  --font:"Helvetica Neue",Helvetica,Arial,sans-serif;
}
*{box-sizing:border-box}
body{margin:0;background:var(--wash);color:var(--ink);font-family:var(--font);
  font-size:15px;line-height:1.5;-webkit-font-smoothing:antialiased;
  font-variant-numeric:tabular-nums;font-feature-settings:"tnum" 1}
button,input,select{font:inherit;color:inherit}
button{cursor:pointer}
:focus-visible{outline:2px solid var(--gold);outline-offset:2px}

/* ---------------------------------------------------------------- connexion */
.gate{min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px;
  background:var(--ink)}
.gate-card{background:var(--paper);width:100%;max-width:400px;padding:38px 36px;
  position:relative;border-top:4px solid var(--gold)}
.gate-card .lang{position:absolute;top:18px;right:18px}
.gate-title{margin:0 0 8px;font-family:var(--display);font-size:30px;line-height:1.1;
  letter-spacing:-.01em;font-weight:400}
.gate-sub{margin:0 0 26px;color:var(--muted);font-size:14px}
.field{display:block;margin-bottom:14px}
.field span{display:block;font-size:12px;color:var(--muted);margin-bottom:5px;
  text-transform:uppercase;letter-spacing:.07em}
.field input{width:100%;padding:10px 12px;border:1px solid var(--rule);background:#fff}
.gate-error{color:var(--rust);font-size:14px;margin:0 0 12px}
.btn-main{padding:12px 20px;background:var(--ink);color:var(--paper);border:0;
  letter-spacing:.02em}
.btn-main:disabled{opacity:.3}

.lang{display:inline-flex;border:1px solid var(--rule)}
.lang button{background:none;border:0;padding:4px 9px;font-size:11px;color:var(--muted);
  letter-spacing:.06em}
.lang button.on{background:var(--ink);color:var(--paper)}

/* ------------------------------------------------------------------ bandeau */
.top{display:flex;align-items:center;gap:26px;padding:14px 26px;background:var(--ink);
  color:var(--paper);position:sticky;top:0;z-index:20;flex-wrap:wrap}
.top-id strong{display:block;font-family:var(--display);font-size:19px;line-height:1.15;
  font-weight:400}
.top-id span{font-size:12px;color:#A79C8C}
.course-pick{display:block;appearance:none;-webkit-appearance:none;background:transparent;
  color:var(--paper);border:0;border-bottom:1px dashed #6B6257;border-radius:0;padding:0 18px 2px 0;
  font-family:var(--display);font-size:19px;line-height:1.15;cursor:pointer;max-width:320px;
  background-image:linear-gradient(45deg,transparent 50%,#A79C8C 50%),linear-gradient(135deg,#A79C8C 50%,transparent 50%);
  background-position:calc(100% - 7px) 55%,calc(100% - 2px) 55%;background-size:5px 5px;background-repeat:no-repeat}
.course-pick option{color:#16120E}
.print-only{display:none !important}
@media print{.print-only{display:block !important}}
.tabs{display:flex;gap:4px;margin-right:auto}
.tabs button{background:none;border:0;padding:8px 14px;color:#A79C8C;
  border-bottom:2px solid transparent;font-size:14px}
.tabs button:hover{color:var(--paper)}
.tabs button.on{color:var(--paper);border-bottom-color:var(--gold);font-weight:600}
.top-right{display:flex;align-items:center;gap:16px;font-size:14px}
.top .lang{border-color:#3A332A}
.top .lang button{color:#A79C8C}
.top .lang button.on{background:var(--gold);color:var(--ink)}
.cohort{display:flex;align-items:center;border:1px solid #3A332A}
.cohort button{width:30px;height:32px;background:none;border:0;color:#A79C8C;font-size:17px;line-height:1}
.cohort input{width:46px;height:32px;border:0;border-left:1px solid #3A332A;
  border-right:1px solid #3A332A;text-align:center;background:none;font-weight:600;
  color:var(--paper)}
.btn-plain{background:none;border:0;color:#A79C8C;text-decoration:underline}
.top-right .btn-plain+.btn-plain{margin-left:12px}
.pwd-overlay{position:fixed;inset:0;z-index:100;background:rgba(20,16,12,.55);display:flex;
  align-items:center;justify-content:center;padding:16px}
.pwd-overlay .gate-card{position:relative}
.pwd-actions{display:flex;align-items:center;gap:16px}
.pwd-actions .btn-plain{color:var(--muted)}

.toolbar{display:flex;gap:8px;justify-content:flex-end;margin-bottom:16px}
.toolbar button{background:none;border:1px solid var(--rule);padding:6px 15px;
  font-size:12px;color:var(--muted);letter-spacing:.05em;text-transform:uppercase}
.toolbar button:hover{border-color:var(--ink);color:var(--ink)}
.toolbar button:disabled{opacity:.4}

.alert{margin:16px 26px;padding:11px 15px;background:var(--paper);
  border-left:3px solid var(--rust);display:flex;justify-content:space-between;
  gap:16px;font-size:14px}
.alert button{background:none;border:0;color:var(--muted)}

.page{max-width:1180px;margin:0 auto;padding:26px 26px 72px}
.page > section{margin-bottom:38px}
.h2{font-family:var(--display);font-size:21px;font-weight:400;margin:0 0 14px;
  display:flex;justify-content:space-between;align-items:baseline;gap:16px}
.h2 span{font-family:var(--font);font-size:12px;color:var(--muted);
  text-transform:uppercase;letter-spacing:.07em}
.note{color:var(--muted);font-size:13px;margin:12px 0 0;max-width:74ch}
.note strong{color:var(--ink)}
.warn{color:var(--rust);font-size:14px}
.warn.block{background:var(--paper);border-left:3px solid var(--gold);
  padding:12px 16px;margin:0 0 32px;color:var(--ink)}
.block-note{background:var(--paper);border-left:3px solid var(--green);padding:14px 16px;
  margin:0 0 32px;max-width:none;font-size:14px}

/* ------------------------------------------------------------------ leviers */
.levers{display:flex;gap:30px;align-items:flex-end;flex-wrap:wrap;margin-bottom:28px}
.lever > span{display:block;font-size:11px;color:var(--muted);margin-bottom:5px;
  text-transform:uppercase;letter-spacing:.07em}
.lever .cohort{border-color:var(--rule);background:var(--paper)}
.lever .cohort button{color:var(--muted)}
.lever .cohort input{border-color:var(--rule);color:var(--ink)}
.lever-note{font-size:13px;color:var(--muted);max-width:44ch;padding-bottom:7px}
.prog-edit{flex:1;min-width:280px}
.prog-edit .lever-label{display:block;font-size:11px;color:var(--muted);margin-bottom:5px;
  letter-spacing:.08em;text-transform:uppercase}
.prog-chips{display:flex;flex-wrap:wrap;gap:8px;align-items:center}
.prog-chip{display:inline-flex;align-items:center;gap:8px;background:var(--paper);
  border:1px solid var(--rule);padding:6px 6px 6px 12px;font-size:14px}
.prog-chip em{font-style:normal;font-size:11px;color:var(--muted);letter-spacing:.04em}
.prog-chip button{background:none;border:0;font-size:17px;line-height:1;color:var(--muted);
  padding:0 6px;cursor:pointer}
.prog-chip button:hover{color:#A23B2A}
.prog-add{border:1px dashed var(--rule);background:transparent;padding:7px 10px;font-size:14px;
  color:var(--ink);cursor:pointer;font-family:inherit}
.prog-add:hover{border-color:var(--ink)}

/* --------------------------------------------------------------------- prix */
.prices{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));
  background:var(--ink);margin-bottom:30px}
.price{padding:26px 26px 28px;color:#A79C8C;border-left:1px solid #2A241C}
.price:first-child{border-left:0}
.price.is-main{background:var(--gold);color:var(--ink)}
.price-label{display:block;font-size:11px;text-transform:uppercase;letter-spacing:.1em;
  opacity:.85}
.price-value{display:block;font-family:var(--display);font-size:54px;line-height:1;
  letter-spacing:-.02em;margin-top:10px;color:var(--paper)}
.price.is-main .price-value{color:var(--ink)}
.price-value em{font-family:var(--font);font-style:normal;font-size:13px;margin-left:8px;
  opacity:.6;letter-spacing:.04em}
.price-usd{display:block;font-size:14px;margin-top:6px;opacity:.8}
.price-note{display:block;font-size:12px;margin-top:12px;opacity:.7;max-width:28ch;
  line-height:1.4}

/* --------------------------------------------------------------------- kpis */
.verdict{display:flex;gap:38px;align-items:flex-end;flex-wrap:wrap;
  border-bottom:2px solid var(--ink);padding-bottom:22px}
.verdict-label{display:block;font-size:11px;color:var(--muted);text-transform:uppercase;
  letter-spacing:.08em}
.verdict-value{display:block;font-family:var(--display);font-size:62px;line-height:1;
  letter-spacing:-.025em;margin-top:8px;color:var(--green)}
.verdict-value em{font-family:var(--font);font-style:normal;font-size:14px;
  color:var(--muted);margin-left:10px;letter-spacing:.03em}
.verdict-foot{display:block;margin-top:8px;font-size:13px;color:var(--muted)}
.kpis{display:grid;grid-template-columns:repeat(3,minmax(110px,1fr));gap:22px;margin:0 0 0 auto}
.kpis dt{font-size:11px;color:var(--muted);text-transform:uppercase;letter-spacing:.07em}
.kpis dd{margin:4px 0 0;font-family:var(--display);font-size:27px;font-weight:400;line-height:1}
.kpis span{font-size:11px;color:var(--muted)}
.kpis.wide{grid-template-columns:repeat(4,minmax(120px,1fr));margin:0 0 34px;
  border-top:1px solid var(--rule);padding-top:22px}

.two{display:grid;grid-template-columns:minmax(0,350px) minmax(0,1fr);gap:36px;margin-bottom:38px}

/* ----------------------------------------------------------------- tableaux */
.table{width:100%;border-collapse:collapse;background:var(--paper)}
.table th{text-align:left;font-size:11px;color:var(--muted);font-weight:400;
  padding:10px 15px;border-bottom:1.5px solid var(--ink);text-transform:uppercase;
  letter-spacing:.06em}
.table td{padding:11px 15px;border-top:1px solid var(--rule);font-size:14px}
.table tbody tr:first-child td{border-top:0}
.table td em{display:block;font-style:normal;font-size:11px;color:var(--muted);
  max-width:58ch;margin-top:3px}
.table .num{text-align:right;white-space:nowrap}
.table .soft,.table .sup{color:var(--muted)}
.table tfoot td{border-top:2px solid var(--ink);font-weight:600}
.pending td{background:#F7F2E9;color:var(--muted)}
.no-price{background:#FBF0E9}
.recovered{background:#EFF3EE}
.split{display:grid;grid-template-columns:minmax(0,1.05fr) minmax(0,1fr);
  align-items:start;background:#F9F4EC;border-top:1px solid var(--rule)}
.split-one{grid-template-columns:1fr}
.split-l{min-width:0;border-right:1px solid var(--rule)}
.split-one .split-l{border-right:0}
.split-r{min-width:0}
.split .method{border-top:0;background:transparent}
.split .leaves td{padding:7px 14px}
@media (max-width:900px){
  .split{grid-template-columns:1fr}
  .split-l{border-right:0;border-bottom:1px solid var(--rule)}
}
.step-shot{display:block;margin:8px 0 4px;max-width:min(100%,340px);width:100%;
  border:1px solid var(--rule);border-radius:2px}
.method .chips{display:flex;flex-wrap:wrap;gap:6px;margin:5px 0 2px}
.method .chips em{font-style:normal;font-size:11px;letter-spacing:.03em;
  color:var(--green);background:#EAF0EA;border:1px solid #D6E2D8;
  padding:1px 7px;border-radius:2px;white-space:nowrap}
.chef-note{display:block;margin:6px 0 2px;padding-left:10px;
  border-left:2px solid var(--gold);font-size:12.5px;line-height:1.5;
  color:var(--muted)}
.chef-note b{font-weight:600;color:var(--gold);font-size:10.5px;
  text-transform:uppercase;letter-spacing:.06em;margin-right:5px}

.step-video{display:block;margin:10px 0 6px;max-width:min(100%,520px)}
.step-video .vid-head{display:flex;flex-wrap:wrap;align-items:baseline;gap:8px;
  margin-bottom:5px}
.step-video .vid-head b{font-family:var(--display);font-weight:400;font-size:14px}
.step-video .vid-head i{font-style:normal;font-size:11.5px;color:var(--muted)}
.step-video .vid-head u{text-decoration:none;font-size:11px;color:var(--gold);
  border:1px solid var(--rule);padding:0 5px;border-radius:2px}
.step-video .vid-frame{display:block;position:relative;aspect-ratio:16/9;
  background:var(--ink);border:1px solid var(--rule);border-radius:2px;
  overflow:hidden}
.step-video iframe{position:absolute;inset:0;width:100%;height:100%;border:0}
.step-video .vid-link{display:none;font-size:12px;color:var(--gold)}
@media print{
  .step-video .vid-frame{display:none}
  .step-video .vid-link{display:inline}
}
.shots{display:flex;flex-wrap:wrap;gap:12px;padding:14px 20px 18px}
.strata .shots{padding:0 20px 18px}
.shots img{height:190px;width:auto;max-width:100%;object-fit:cover;
  border:1px solid var(--rule);border-radius:2px;background:var(--wash)}
h1.recipe-title + .note + .shots{padding:4px 0 22px}
@media print{
  .split{grid-template-columns:minmax(0,1.05fr) minmax(0,1fr)}
  .shots img{height:150px}
  .step-shot{max-width:220px}
}
.recovered .l-unit,.recovered td:nth-child(3){color:var(--green);font-style:italic}
.pnl .blockhead td{font-weight:600;border-top:1px solid var(--rule)}
.pnl .blockhead.total td{border-top:2px solid var(--ink)}
.pnl td.sub{padding-left:30px;color:var(--muted);border-top:0}
.pnl tbody tr td{border-top:0}
.pnl tfoot td{font-family:var(--display);font-size:16px}
.negtext{color:var(--rust)}
.strong-num{font-weight:600;color:var(--gold)}
.table tfoot tr.est td{border-top:1px solid var(--rule);font-weight:400;color:var(--muted)}
.table tfoot tr.est td em{color:var(--muted)}
.table tfoot tr.soft-foot td{border-top:1px solid var(--rule);font-weight:400;color:var(--muted)}
.table tfoot tr.grand td{border-top:2px solid var(--ink);font-family:var(--display);font-size:16px}

.chart{margin:0;background:var(--paper);padding:14px 10px 4px}
.chart figcaption{font-size:12px;color:var(--muted);padding:10px;max-width:66ch}

.gridwrap{overflow-x:auto}
.grid th{text-align:right}
.grid th:first-child{text-align:left}
.grid th em{display:block;font-style:normal;font-size:10px;font-weight:400;opacity:.65;
  letter-spacing:0;text-transform:none}
.grid .cell em{display:block;font-style:normal;font-size:11px;color:var(--muted)}
.grid .neg{background:#F7E3DC;color:var(--rust)}
.grid .thin{background:#FBF3E4}
.grid .good{background:#E9EFEA}
.grid tr.here td{box-shadow:inset 0 0 0 1.5px var(--ink)}

.params{background:var(--paper);padding:18px 20px;border-top:2px solid var(--ink)}
.params-toggle{background:none;border:0;font-family:var(--display);font-size:18px;padding:0}
.params-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:0 36px}
.params-group{margin-top:20px}
.params-group h3{font-size:11px;color:var(--muted);font-weight:400;margin:0 0 8px;
  text-transform:uppercase;letter-spacing:.07em}
.param{display:flex;justify-content:space-between;align-items:center;gap:16px;
  padding:6px 0;border-bottom:1px solid var(--rule);font-size:14px;max-width:520px}
.param-in{display:flex;align-items:center;gap:8px}
.param-in input{width:92px;padding:5px 9px;border:1px solid var(--rule);text-align:right}
.param-in em{font-style:normal;font-size:12px;color:var(--muted);width:88px}
.params .btn-main{margin-top:20px}

/* ----------------------------------------------------------------- recettes */
.pickers{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:24px}
.pick{background:var(--paper);border:1px solid var(--rule);padding:8px 14px;font-size:14px}
.pick:hover{border-color:var(--ink)}
.pick.on{background:var(--ink);color:var(--paper);border-color:var(--ink)}
/* ------------------------------------------------------------------ import */
.imp-open{display:flex;justify-content:flex-end;margin:-6px 0 10px}
.imp-head{display:flex;align-items:baseline;justify-content:space-between;gap:16px;margin-bottom:10px}
.imp .alert{margin:12px 0}
.imp-upload{display:flex;gap:18px;align-items:flex-end;flex-wrap:wrap;background:var(--paper);
  border:1px solid var(--rule);padding:18px;margin:16px 0 30px}
.imp-upload label,.imp-fields label{display:flex;flex-direction:column;gap:5px;font-size:14px}
.imp-upload label > span,.imp-fields label > span{font-size:11px;color:var(--muted);
  text-transform:uppercase;letter-spacing:.07em}
.imp input,.imp select,.imp textarea{font:inherit;font-size:14px;border:1px solid var(--rule);
  background:var(--paper);color:var(--ink);padding:7px 9px;border-radius:0}
.imp-fields input,.imp-fields textarea,.imp-upload input{width:100%;min-width:0;box-sizing:border-box}
.imp-fields label,.imp-upload label{min-width:0}
.imp-scroll{overflow-x:auto}
.imp-scroll .imp-table{min-width:640px}
.imp-upload input[type=file]{border:1px dashed var(--rule);padding:9px}
.imp-go{background:var(--ink);color:var(--paper);border:1px solid var(--ink);padding:9px 18px;
  font-size:14px;cursor:pointer}
.imp-go:disabled{opacity:.4;cursor:not-allowed}
.imp-dl{text-align:right;white-space:nowrap}
.imp-dl .btn-plain{margin-left:10px}
.imp-fields{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:14px;margin:18px 0}
.imp-fields .wide{grid-column:span 2}
.imp-summary{display:flex;gap:22px;flex-wrap:wrap;font-size:13px;color:var(--muted);
  border-top:1px solid var(--rule);border-bottom:1px solid var(--rule);padding:10px 0;margin:6px 0 22px}
.imp-summary span{display:inline-flex;align-items:center;gap:6px}
.imp-dot{display:inline-block;width:9px;height:9px;border-radius:50%;margin-right:8px;
  vertical-align:middle;background:var(--muted)}
.imp-dot.ok{background:var(--green)}
.imp-dot.check{background:var(--gold-lt)}
.imp-dot.suggest{background:#8FA7C4}
.imp-dot.new{background:#3F6EA8}
.imp-dot.section{background:#7A5AA6}
.imp-dot.qty{background:var(--rust)}
.imp-section{margin-bottom:26px}
.imp-sec-head{display:flex;align-items:baseline;justify-content:space-between;gap:12px;margin-bottom:6px;flex-wrap:wrap}
.imp-reuse{max-width:380px;font-size:13px !important;color:var(--muted) !important}
.imp-reuse.on{border-color:var(--green) !important;color:var(--green) !important;font-weight:500}
.imp-reused{background:var(--paper);border:1px solid var(--rule);border-left:3px solid var(--green);
  padding:12px 14px;margin:0}
.imp-sec-name{font-family:var(--display) !important;font-size:20px !important;border:0 !important;
  border-bottom:1px dashed var(--rule) !important;background:transparent !important;padding:2px 0 !important;
  min-width:36%;flex:1}
.imp-table td{vertical-align:top}
.imp-table td:first-child{width:34%}
.imp-table td:nth-child(2){width:120px;white-space:nowrap}
.imp-raw{font-weight:500}
.imp-line em{display:block;font-style:normal;font-size:12px;color:var(--muted);margin-top:2px}
.imp-line em.warn{color:#9A6A12}
.imp-line em.err{color:var(--rust)}
.imp-line.ignored td{opacity:.55}
.imp-line.ignored td:last-child{opacity:1}
.imp-line.bad{background:#FBF1EE}
.imp-g{width:74px;text-align:right}
.imp-unit{font-size:12px;color:var(--muted);margin-left:4px}
.imp-pick{width:100%;max-width:440px}
.imp-sugg{display:block;margin-top:6px;background:none;border:1px dashed #8FA7C4;color:#2F5A8A;
  padding:4px 9px;font-size:12px;cursor:pointer}
.imp-new{display:flex;gap:6px;flex-wrap:wrap;margin-top:6px}
.imp-new input{flex:1;min-width:150px}
.imp-new .imp-price{flex:0 0 140px;min-width:0}
.imp-steps{margin-top:6px;font-size:13px;color:var(--muted)}
.imp-steps summary{cursor:pointer}
.imp-steps ol{margin:8px 0 0 18px;padding:0;color:var(--ink)}
.imp-steps li{margin-bottom:4px}
.imp-foot{display:flex;justify-content:flex-end;align-items:center;gap:14px;flex-wrap:wrap;
  border-top:1px solid var(--rule);padding-top:16px;margin-top:10px}
.imp-issues{flex:1 1 100%;margin:0 0 6px;padding-left:18px;color:var(--rust);font-size:13px}
.imp-warn{color:#9A6A12}
@media (max-width:760px){
  .imp-fields{grid-template-columns:1fr 1fr}
  .imp-upload label{flex:1 1 100%}
  .imp-head .recipe-title{font-size:26px}
  .imp-table td:first-child{width:auto}
}

.chefs{display:flex;gap:28px;flex-wrap:wrap;border-bottom:1px solid var(--rule);margin-bottom:22px}
.chef-tab{background:none;border:0;border-bottom:2px solid transparent;padding:0 0 10px;
  margin-bottom:-1px;font-family:var(--display);font-size:20px;color:var(--ink);opacity:.55;cursor:pointer}
.chef-tab span{font-family:inherit;font-size:12px;opacity:.8;margin-left:4px;vertical-align:super}
.chef-tab:hover{opacity:.85}
.chef-tab.on{opacity:1;border-bottom-color:var(--ink)}
.chef-group .pickers{margin-bottom:18px}
.chef-group:last-child .pickers{margin-bottom:24px}
.chef-label{font-size:11px;letter-spacing:.12em;text-transform:uppercase;font-weight:600;
  margin:0 0 8px;opacity:.6}
.ip-note{font-size:11.5px;line-height:1.5;color:var(--muted);font-style:italic;max-width:78ch;
  border-left:2px solid var(--gold);padding:2px 0 2px 10px;margin:6px 0 14px}
@media print{.ip-note{color:#333;border-left-color:#999}}
.chef-by{font-size:11px;letter-spacing:.12em;text-transform:uppercase;margin:0 0 6px;opacity:.6}
.recipe-title{font-family:var(--display);font-size:38px;font-weight:400;margin:0;
  letter-spacing:-.02em;max-width:18ch;line-height:1.08}

.strata{margin-top:26px;background:var(--paper)}
.stratum + .stratum{border-top:1px solid var(--rule)}
.stratum-band{display:block;width:100%;text-align:left;background:none;border:0;padding:0;position:relative}
.band-fill{position:absolute;left:0;top:0;bottom:0;width:var(--share);background:var(--band);opacity:.15}
.stratum.is-open .band-fill{opacity:.26}
.stratum-band::before{content:"";position:absolute;left:0;top:0;bottom:0;width:4px;background:var(--band)}
.band-body{position:relative;display:flex;justify-content:space-between;gap:16px;
  align-items:baseline;padding:15px 16px 15px 20px}
.band-name{font-weight:600}
.band-count{display:block;font-weight:400;font-size:12px;color:var(--muted);margin-top:2px}
.band-figures{display:flex;gap:20px;align-items:baseline;white-space:nowrap}
.band-qty{color:var(--gold);font-weight:600}
.band-cost{min-width:64px;text-align:right;font-weight:600}
.leaves{width:100%;border-collapse:collapse;background:#F9F4EC}
.leaves td{padding:8px 16px;border-top:1px solid var(--rule);font-size:14px;vertical-align:top}
.leaves .l-name em{display:block;font-style:normal;font-size:11px;color:var(--muted)}
.leaves .l-qty{color:var(--gold);white-space:nowrap;width:92px}
.leaves .l-unit{color:var(--muted);white-space:nowrap;width:120px}
.leaves .l-sup{color:var(--muted)}
.leaves .l-cost{text-align:right;white-space:nowrap;width:82px}
.leaves .l-open{background:none;border:0;padding:0;font:inherit;color:inherit;cursor:pointer;
  text-align:left;display:inline-flex;align-items:baseline;gap:7px}
.leaves .l-open:hover{color:var(--gold)}
.leaves .l-caret{display:inline-block;width:13px;color:var(--gold);font-size:13px}
.leaves .l-count{font-size:11px;color:var(--muted)}
.leaves .l-nest > td{padding:0 0 0 30px;border-top:0;background:var(--wash)}
.leaves-in{background:var(--wash)}
.leaves-in td{font-size:13.5px}
.method-in{background:transparent;padding:10px 16px 14px}

.sliders{display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));
  gap:20px 40px;background:var(--paper);padding:24px 26px;border-top:2px solid var(--ink)}
.slider{display:block}
.slider-top{display:flex;justify-content:space-between;align-items:baseline;gap:12px;
  margin-bottom:8px}
.slider-top span{font-size:12px;color:var(--muted);text-transform:uppercase;letter-spacing:.06em}
.slider-top strong{font-family:var(--display);font-size:20px;font-weight:400;white-space:nowrap}
.slider-top em{font-family:var(--font);font-style:normal;font-size:12px;color:var(--muted);
  margin-left:3px}
.slider input[type=range]{width:100%;-webkit-appearance:none;appearance:none;height:2px;
  background:var(--rule);outline:none}
.slider input[type=range]::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;
  width:17px;height:17px;border-radius:50%;background:var(--gold);cursor:pointer;
  border:2px solid var(--paper);box-shadow:0 0 0 1px var(--gold)}
.slider input[type=range]::-moz-range-thumb{width:15px;height:15px;border-radius:50%;
  background:var(--gold);cursor:pointer;border:2px solid var(--paper)}


.method{background:#F9F4EC;padding:14px 20px 16px;border-top:1px solid var(--rule)}
.method h4{margin:0 0 8px;font-size:11px;text-transform:uppercase;letter-spacing:.07em;
  color:var(--muted);font-weight:400;display:flex;justify-content:space-between;
  align-items:baseline;gap:12px}
.method h4 span{text-transform:none;letter-spacing:0}
.method ol{margin:0;padding-left:20px;max-width:88ch}
.method li{margin:5px 0;font-size:13.5px;line-height:1.5}
.method li::marker{color:var(--gold);font-size:11px}
.method.standalone{margin-top:26px;background:var(--paper);border-top:2px solid var(--ink);
  padding:18px 22px 20px}
.baking{margin-top:26px;background:var(--paper);border-left:3px solid var(--gold);
  padding:14px 20px}
.baking h4{margin:0 0 6px;font-size:11px;text-transform:uppercase;letter-spacing:.07em;
  color:var(--muted);font-weight:400}
.baking ul{margin:0;padding-left:18px}
.baking li{margin:3px 0;font-size:14px}

@media (max-width:980px){
  .two{grid-template-columns:1fr}
  .kpis{grid-template-columns:repeat(2,1fr);margin:20px 0 0}
  .prices{grid-template-columns:1fr}
  .price{border-left:0;border-top:1px solid #2A241C}
  .kpis.wide{grid-template-columns:repeat(2,1fr)}
  .verdict-value{font-size:44px}
  .price-value{font-size:40px}
  .recipe-title{font-size:28px}
  .tabs{order:3;width:100%;margin:0}
  .leaves .l-sup,.table .sup{display:none}
  .page{padding:20px 14px 48px}
  .sliders{grid-template-columns:1fr;padding:18px 16px}
}

@media print{
  body{background:#fff;font-size:11px;color:#000}
  .no-print{display:none!important}
  .top{position:static;background:#fff;color:#000;border-bottom:2px solid #000;
    padding:0 0 10px;margin-bottom:16px}
  .top-id span{color:#555}
  .page{max-width:none;padding:0}
  .page > section{margin-bottom:20px;break-inside:avoid}
  .two{grid-template-columns:1fr;gap:16px}
  .prices{background:#fff;border:1px solid #000;break-inside:avoid}
  .price{color:#000;border-left:1px solid #999}
  .price-value{color:#000}
  .price.is-main{background:#fff;outline:2px solid #000;outline-offset:-2px}
  .verdict-value,.price-value{font-size:28px}
  .table th{background:#eee;color:#000}
  .chart{border:1px solid #999}
  .strata{break-inside:avoid}
  .method,.baking{break-inside:avoid}
  .sliders{display:none}
  @page{margin:14mm}
}
@media (prefers-reduced-motion:reduce){*{transition:none!important;animation:none!important}}
    `}</style>
  );
}
